import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { openPrintWindow } from '@/utils/printHelper';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Calculator, Layers, Plus, Save, Trash2, Box, Building2, Check, ChevronsUpDown, Edit, MessageSquare, CheckCircle, Clock, Eye, Lock, Copy, Printer, FileText, Factory, Package, Scale, X, Archive, RotateCcw, ArrowLeft, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { PasswordInput } from '@/components/ui/password-input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Updated Shade options
const SHADE_OPTIONS = [
  'Plain Kraft (Domestic)',
  'GYT Kraft (Domestic)',
  'Duplex Paper',
  'Imported Paper',
  'South India Paper'
];

// Production Type options
const PRODUCTION_TYPE_OPTIONS = [
  'Manual Production',
  'Die Cut Production'
];

// Approval password - validated via backend /api/auth/validate
// Page access password - validated via backend /api/auth/validate

const WorkOrderQuotation = () => {
  const navigate = useNavigate();
  
  // Page access control - check session cookie via backend
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Render gate: while we're still resolving "is this user allowed in?",
  // render NOTHING (a tiny spinner). Otherwise the locked-state Card with
  // the password dialog flashes for ~200ms on every admin page-load before
  // the async checkSession resolves and flips us into the unlocked view.
  // Matches the AuthCheckSkeleton pattern used by the module layouts.
  const [authChecking, setAuthChecking] = useState(true);
  // Admin & master accounts get a free pass on every password gate in this
  // page (status approval, permanent delete, archive restore, etc).
  // Customer ask Feb 2026: "for admin & Creator login skip all the passwords".
  const [bypassAllPasswords, setBypassAllPasswords] = useState(false);
  const [pagePasswordInput, setPagePasswordInput] = useState('');
  const [pagePasswordError, setPagePasswordError] = useState('');
  
  useEffect(() => {
    // Check if already authenticated via session
    const checkAuth = async () => {
      try {
        const { checkSession } = await import("@/utils/auth");
        const session = await checkSession();
        if (session.authenticated && (session.roles.includes("workorder") || session.roles.includes("admin") || session.is_master)) {
          setIsAuthenticated(true);
        }
        const roles = session.roles || [];
        if (session.is_master || roles.includes("admin")) {
          setBypassAllPasswords(true);
        }
      } catch (err) {
        console.error("checkAuth session lookup failed:", err);
      } finally {
        // Always release the render gate — if auth failed, the locked-state
        // Card will render. The point is to never render it BEFORE the
        // session probe resolves.
        setAuthChecking(false);
      }
    };
    checkAuth();
  }, []);
  
  // Main page company selection
  const [selectedPageCompany, setSelectedPageCompany] = useState('');
  const [pageCompanyOpen, setPageCompanyOpen] = useState(false);
  
  const [showDialog, setShowDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingQuotation, setViewingQuotation] = useState(null);
  const [savedQuotations, setSavedQuotations] = useState([]);
  const [approvedQuotations, setApprovedQuotations] = useState([]);
  // Sent quotations log — full history per quotation (every send creates a
  // row). Populated from GET /api/quotations/sent-log. Added Feb 2026.
  const [sentLog, setSentLog] = useState([]);
  const [archivedQuotations, setArchivedQuotations] = useState([]);
  const [allArchivedQuotations, setAllArchivedQuotations] = useState([]);
  const [allQuotations, setAllQuotations] = useState([]);
  const [allApprovedQuotations, setAllApprovedQuotations] = useState([]);
  const [showAllQuotations, setShowAllQuotations] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState(null);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [currentComment, setCurrentComment] = useState('');
  const [commentingQuotationId, setCommentingQuotationId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  
  // Password dialog for status change
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [pendingStatusChange, setPendingStatusChange] = useState(null);

  // Create Quotation Dialog (simplified for selecting from existing quotations)
  const [showCreateQuotationDialog, setShowCreateQuotationDialog] = useState(false);
  const [createQuotationCompany, setCreateQuotationCompany] = useState('');
  const [createQuotationCompanyOpen, setCreateQuotationCompanyOpen] = useState(false);
  const [availableQuotationsForCompany, setAvailableQuotationsForCompany] = useState([]);
  const [selectedQuotationForPrint, setSelectedQuotationForPrint] = useState('');
  const [quotationItemOpen, setQuotationItemOpen] = useState(false);

  // Form company selection
  const [selectedFormCompany, setSelectedFormCompany] = useState('');
  const [formCompanyOpen, setFormCompanyOpen] = useState(false);
  const [companyItems, setCompanyItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState('');
  const [itemOpen, setItemOpen] = useState(false);
  
  // Product Specifications for Item Name dropdown (connected to Prod & Materials Info)
  const [productSpecs, setProductSpecs] = useState([]);
  const [filteredProductSpecs, setFilteredProductSpecs] = useState([]);
  const [itemNameSearch, setItemNameSearch] = useState('');
  const [useManualItemName, setUseManualItemName] = useState(false);

  // Product Type Selection (Box, Layer, Partition)
  const [productType, setProductType] = useState('Box');
  const [productTypeOpen, setProductTypeOpen] = useState(false);
  const [customProductTypes, setCustomProductTypes] = useState([]);
  const [newProductType, setNewProductType] = useState('');
  const [showAddProductType, setShowAddProductType] = useState(false);

  // Default product types
  const DEFAULT_PRODUCT_TYPES = ['Box', 'Layer', 'Partition'];
  const allProductTypes = [...DEFAULT_PRODUCT_TYPES, ...customProductTypes];

  // Quotation form state
  const [itemName, setItemName] = useState('');
  const [productionType, setProductionType] = useState('Manual Production');
  const [productionTypeOpen, setProductionTypeOpen] = useState(false);
  const [dimensions, setDimensions] = useState({ length: '', breadth: '', height: '' });
  const [dimensionUnit, setDimensionUnit] = useState('mm');
  const [reelSize, setReelSize] = useState('');
  const [cuttingSize, setCuttingSize] = useState('');
  const [jointType, setJointType] = useState('Single');
  
  // Wall-2 for Four joint type (Wall-1 uses reelSize and cuttingSize above)
  const [wall2ReelSize, setWall2ReelSize] = useState('');
  const [wall2CuttingSize, setWall2CuttingSize] = useState('');
  
  // Up's (number of boxes per production)
  const [ups, setUps] = useState('1');
  const [upsOptions, setUpsOptions] = useState(['1', '2', '3', '4', '5', '6']);
  const [showAddUps, setShowAddUps] = useState(false);
  const [newUpsValue, setNewUpsValue] = useState('');
  
  // Other Costs (array of {title, amount})
  const [otherCosts, setOtherCosts] = useState([]);
  
  const [numberOfPartitions, setNumberOfPartitions] = useState('1'); // For Partition product type
  const [moq, setMoq] = useState('');
  const [plyCount, setPlyCount] = useState('3');
  
  // Ply configuration (with paper price per ply)
  const [plyConfig, setPlyConfig] = useState([]);
  
  // Pricing inputs (removed single paperPrice - now per ply)
  const [profitMargin, setProfitMargin] = useState('30');
  const [profitMarginType, setProfitMarginType] = useState('rupees');
  const [printingPrice, setPrintingPrice] = useState('0');
  const [laminationEnabled, setLaminationEnabled] = useState(false);
  const [laminationVendorCost, setLaminationVendorCost] = useState('');
  const [transportCost, setTransportCost] = useState('');
  
  // Wastage percentage (default 5%)
  const [wastagePercent, setWastagePercent] = useState('5');
  
  // Total Corrugated Rolls
  const [totalCorrugatedRolls, setTotalCorrugatedRolls] = useState('');
  
  // Calculated values (including weight)
  const [calculatedValues, setCalculatedValues] = useState({
    totalGSM: 0,
    baseValue: 0,
    laminationCost: 0,
    transportCostPerUnit: 0,
    finalBoxPrice: 0,
    boxWeight: 0,
    plyPrices: [],
    // Four joint specific
    wall1Price: 0,
    wall2Price: 0
  });

  // Quotation counter for numbering
  const [quotationCount, setQuotationCount] = useState(0);

  useEffect(() => {
    const initFetch = async () => {
      try { const r = await axios.get(`${API}/materials`); setMaterials(r.data); } catch (e) { console.error("Failed to load materials:", e); }
      try { const r = await axios.get(`${API}/wo-customers`); setCustomers(r.data); } catch (e) { console.error("Failed to load customers:", e); }
      try {
        const r = await axios.get(`${API}/quotations`);
        let maxNum = 0;
        r.data.forEach(q => {
          if (q.quotation_number && q.quotation_number.startsWith('QTCPI-')) {
            const numPart = parseInt(q.quotation_number.replace('QTCPI-', ''), 10);
            if (!isNaN(numPart) && numPart > maxNum) maxNum = numPart;
          }
        });
        setQuotationCount(maxNum);
        setAllQuotations(r.data.filter(q => q.status !== 'approved'));
        setAllApprovedQuotations(r.data.filter(q => q.status === 'approved'));
      } catch (e) { console.error("Failed to load quotations:", e); }
      try { const r = await axios.get(`${API}/quotations/archived`); setAllArchivedQuotations(r.data || []); } catch (e) { console.error("Failed to load archived quotations:", e); setAllArchivedQuotations([]); }
      try { const r = await axios.get(`${API}/product-specifications`); setProductSpecs(r.data); } catch (e) { console.error("Failed to load product specs:", e); }
    };
    initFetch();
  }, []);

  // Fetch quotations when page company changes
  useEffect(() => {
    if (selectedPageCompany) {
      fetchSavedQuotations();
    } else {
      setSavedQuotations([]);
      setApprovedQuotations([]);
    }
  }, [selectedPageCompany]);

  // Fetch items from customers when form company changes
  useEffect(() => {
    if (selectedFormCompany) {
      // Get items from the customer record
      const customer = customers.find(c => c.company_name?.toLowerCase() === selectedFormCompany.toLowerCase());
      if (customer && customer.items && customer.items.length > 0) {
        setCompanyItems(customer.items.map(item => ({
          name: item.item_name,
          price: item.price || 0
        })));
      } else {
        setCompanyItems([]);
      }
      
      // Filter product specs by selected company
      const filtered = productSpecs.filter(spec => 
        spec.company_name?.toLowerCase() === selectedFormCompany.toLowerCase()
      );
      setFilteredProductSpecs(filtered);
    } else {
      setCompanyItems([]);
      setFilteredProductSpecs([]);
    }
    // Skip clearing item/name fields when this effect run is triggered by
    // handleEditQuotation/handleCloneQuotation. Without this guard the load
    // sequence (set company → set item name → set selectedItem) is unwound
    // here because React fires this effect AFTER the load batch, wiping the
    // values the user just clicked Edit to see.
    if (loadingFromEditRef.current) {
      loadingFromEditRef.current = false;
      return;
    }
    setSelectedItem('');
    setItemName('');
    setUseManualItemName(false);
    setItemNameSearch('');
  }, [selectedFormCompany, customers, productSpecs]);

  // Fetch quotations for Create Quotation dialog when company changes
  useEffect(() => {
    if (createQuotationCompany) {
      fetchQuotationsForCreateDialog(createQuotationCompany);
    } else {
      setAvailableQuotationsForCompany([]);
    }
    setSelectedQuotationForPrint('');
  }, [createQuotationCompany]);

  // Initialize ply configuration when ply count changes (with paper price per ply).
  // CRITICAL: skip when the existing plyConfig already has the right shape — this
  // prevents the regen from wiping ply data that was just loaded by
  // handleEditQuotation/handleCloneQuotation (which sets plyCount + plyConfig in
  // the same batch; without this guard the effect fires post-batch and clobbers
  // the loaded config with empty placeholders).
  useEffect(() => {
    const ply = parseInt(plyCount);
    if (!ply || Number.isNaN(ply)) return;
    const linerCount = Math.ceil((ply + 1) / 2);
    const fluteCount = Math.floor(ply / 2);
    const expectedLen = linerCount + fluteCount;
    if (plyConfig.length === expectedLen) return; // already matches — don't wipe.

    const newConfig = [];
    for (let i = 0; i < linerCount; i++) {
      newConfig.push({
        type: 'liner',
        name: `Liner ${i + 1}`,
        shade: '',
        gsm: '',
        paperPrice: ''
      });
      if (i < fluteCount) {
        newConfig.push({
          type: 'flute',
          name: `Flute ${i + 1}`,
          shade: '',
          gsm: '',
          paperPrice: ''
        });
      }
    }
    setPlyConfig(newConfig);
  }, [plyCount]); // intentionally only on plyCount; guard above prevents wiping loaded edits

  // Track whether the user has manually edited Reel/Cutting in the current
  // form session. If they have, OR if we're editing an existing quotation
  // that already has reel/cutting populated, we MUST stop the auto-recompute
  // — otherwise it overwrites the loaded/typed values whenever the user
  // tweaks dimensions or MOQ. This also fixes the clone→edit bug where the
  // cloned quotation's reel/cutting were getting clobbered on dialog open.
  const reelManuallyEditedRef = useRef(false);
  // True while handleEditQuotation/handleCloneQuotation is hydrating the form.
  // The company-change effect checks this ref and skips its "clear item name"
  // cleanup so loaded values aren't wiped by an effect that fires after the
  // load batch (same class of bug as the ply-config wipe).
  const loadingFromEditRef = useRef(false);

  // Auto-calculate Reel Size & Cutting Size from dimensions — ONLY on the
  // very first dimension entry for a brand-new quotation. Once a value
  // exists (either typed manually or loaded from an existing/cloned record),
  // dimension/MOQ changes will NOT overwrite Reel & Cutting.
  // RSC Box: Reel = B + H, Cutting = 2(L + B) + joint allowance
  useEffect(() => {
    if (productType === 'Partition') return; // Partitions don't use dimension-based reel/cutting
    // Respect manual edits and loaded values — never overwrite.
    if (reelManuallyEditedRef.current) return;
    if (reelSize !== '' || cuttingSize !== '') return;
    
    const L = parseFloat(dimensions.length) || 0;
    const B = parseFloat(dimensions.breadth) || 0;
    const H = parseFloat(dimensions.height) || 0;
    
    if (L > 0 && B > 0 && H > 0) {
      // Joint allowance based on joint type (in same unit as dimensions)
      let jointAllowance = 0;
      if (dimensionUnit === 'mm') jointAllowance = 40;
      else if (dimensionUnit === 'cm') jointAllowance = 4;
      else jointAllowance = 1.5; // inches
      
      const calculatedReel = B + H;
      const calculatedCutting = 2 * (L + B) + jointAllowance;
      
      setReelSize(calculatedReel.toString());
      setCuttingSize(calculatedCutting.toString());
    }
  }, [dimensions.length, dimensions.breadth, dimensions.height, dimensionUnit, jointType, productType, reelSize, cuttingSize]);

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(`${API}/materials`);
      setMaterials(response.data);
    } catch (error) {
      console.error('Error fetching materials:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/wo-customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchProductSpecs = async () => {
    try {
      const response = await axios.get(`${API}/product-specifications`);
      setProductSpecs(response.data);
    } catch (error) {
      console.error('Error fetching product specifications:', error);
    }
  };

  const fetchQuotationCount = async () => {
    try {
      const response = await axios.get(`${API}/quotations`);
      // Find the highest QTCPI number from existing quotations
      let maxNum = 0;
      response.data.forEach(q => {
        if (q.quotation_number && q.quotation_number.startsWith('QTCPI-')) {
          const numPart = parseInt(q.quotation_number.replace('QTCPI-', ''), 10);
          if (!isNaN(numPart) && numPart > maxNum) {
            maxNum = numPart;
          }
        }
      });
      setQuotationCount(maxNum);
    } catch (error) {
      console.error('Error fetching quotation count:', error);
    }
  };

  const fetchSavedQuotations = async () => {
    try {
      const response = await axios.get(`${API}/quotations`);
      const all = response.data.filter(q => 
        q.company_name?.toLowerCase() === selectedPageCompany.toLowerCase()
      );
      setSavedQuotations(all.filter(q => q.status !== 'approved'));
      setApprovedQuotations(all.filter(q => q.status === 'approved'));
      
      // Also update all quotations for "View All" mode
      setAllQuotations(response.data.filter(q => q.status !== 'approved'));
      setAllApprovedQuotations(response.data.filter(q => q.status === 'approved'));
      
      // Fetch archived quotations
      try {
        const archivedResponse = await axios.get(`${API}/quotations/archived`);
        // Store all archived quotations
        setAllArchivedQuotations(archivedResponse.data);
        // Filter by company for company-specific view
        const archivedAll = archivedResponse.data.filter(q => 
          q.company_name?.toLowerCase() === selectedPageCompany.toLowerCase()
        );
        setArchivedQuotations(archivedAll);
      } catch (archiveError) {
        console.error('Error fetching archived quotations:', archiveError);
      }
    } catch (error) {
      console.error('Error fetching quotations:', error);
    }
    // Also refresh the sent-quotations log (best-effort — UI still works if
    // this fails). One row per send; same quotation can appear multiple
    // times if it was emailed more than once.
    try {
      const sentResp = await axios.get(`${API}/quotation-sent-log`, { params: { limit: 500 } });
      setSentLog(sentResp.data || []);
    } catch (sentErr) {
      console.error('Error fetching sent log:', sentErr);
    }
  };

  const fetchAllQuotations = async () => {
    try {
      const response = await axios.get(`${API}/quotations`);
      setAllQuotations(response.data.filter(q => q.status !== 'approved'));
      setAllApprovedQuotations(response.data.filter(q => q.status === 'approved'));
    } catch (error) {
      console.error('Error fetching all quotations:', error);
    }
    
    // Fetch archived quotations separately for reliability
    try {
      const archivedResponse = await axios.get(`${API}/quotations/archived`);
      setAllArchivedQuotations(archivedResponse.data || []);
    } catch (archiveError) {
      console.error('Error fetching archived quotations:', archiveError);
      setAllArchivedQuotations([]);
    }
  };

  const fetchQuotationsForCreateDialog = async (companyName) => {
    try {
      const response = await axios.get(`${API}/quotations`);
      // Filter to get in_process quotations for the selected company
      const filtered = response.data.filter(q => 
        q.company_name?.toLowerCase() === companyName.toLowerCase() &&
        (q.status === 'in_process' || q.status === 'pending')
      );
      setAvailableQuotationsForCompany(filtered);
    } catch (error) {
      console.error('Error fetching quotations for dialog:', error);
    }
  };

  const updatePlyConfig = (index, field, value) => {
    const newConfig = [...plyConfig];
    newConfig[index] = { ...newConfig[index], [field]: value };
    setPlyConfig(newConfig);
  };

  const calculatePrice = useCallback(() => {
    // Calculate Total GSM with flute multiplier (150%)
    let totalGSM = 0;
    plyConfig.forEach(ply => {
      const gsm = parseFloat(ply.gsm) || 0;
      if (ply.type === 'flute') {
        totalGSM += gsm * 1.5;
      } else {
        totalGSM += gsm;
      }
    });

    // Convert reel/cutting sizes to inches based on dimension unit
    // Formula constant 1550 assumes inches (1 sq meter ≈ 1550 sq inches)
    const toInches = (val) => {
      const v = parseFloat(val) || 0;
      if (dimensionUnit === 'mm') return v / 25.4;
      if (dimensionUnit === 'cm') return v / 2.54;
      return v; // already inches
    };
    const reel = toInches(reelSize);
    const cutting = toInches(cuttingSize);
    const reel2 = toInches(wall2ReelSize);
    const cutting2 = toInches(wall2CuttingSize);
    const margin = parseFloat(profitMargin) || 0;
    // For Partition, printing price is 0
    const printing = productType === 'Partition' ? 0 : (parseFloat(printingPrice) || 0);
    const moqVal = parseFloat(moq) || 1;
    const transport = parseFloat(transportCost) || 0;
    const laminationVendor = parseFloat(laminationVendorCost) || 0;
    const numPartitions = parseInt(numberOfPartitions) || 1;
    const upsValue = parseInt(ups) || 1;
    
    // Calculate total other costs
    const totalOtherCosts = otherCosts.reduce((sum, cost) => sum + (parseFloat(cost.amount) || 0), 0);
    
    // Wastage multiplier (default 5% = 1.05)
    const wastage = parseFloat(wastagePercent) || 5;
    const wastageMultiplier = 1 + (wastage / 100);

    // For Partition: use number of partitions as multiplier
    // For Box/Layer with Four joint: we calculate Wall-1 and Wall-2 separately
    // For Single/Double: use joint type multiplier
    let jointMultiplier = 1;
    if (productType === 'Partition') {
      jointMultiplier = numPartitions;
    } else if (jointType !== 'Four') {
      jointMultiplier = jointType === 'Double' ? 2 : 1;
    }

    // Helper function to calculate price for a single wall
    const calculateWallPrice = (wallReel, wallCutting, wallJointMultiplier) => {
      // Lamination cost: Reel Size × Cutting Size / 100 × Lamination Vendor Cost
      const wallLaminationCost = laminationEnabled ? (wallReel * wallCutting / 100) * laminationVendor : 0;

      const wallPlyPrices = [];
      let wallTotalPlyPrice = 0;

      plyConfig.forEach((ply, index) => {
        const gsm = parseFloat(ply.gsm) || 0;
        const plyPaperPrice = parseFloat(ply.paperPrice) || 0;
        
        // Apply flute multiplier to GSM
        const effectiveGSM = ply.type === 'flute' ? gsm * 1.5 : gsm;
        
        // Calculate profit in rupees for this ply
        let profitInRupees = margin;
        if (profitMarginType === 'percentage') {
          profitInRupees = (plyPaperPrice * margin) / 100;
        }
        
        // Base calculation for this ply: (((GSM × Reel × Cutting) / 1550) × Joint) × Wastage
        let baseValue = (effectiveGSM * wallReel * wallCutting) / 1550;
        baseValue = baseValue * wallJointMultiplier;
        baseValue = baseValue * wastageMultiplier; // Apply wastage percentage
        
        // Price for this ply: (baseValue × (Paper Price + Profit)) ÷ 1000
        const plyPrice = (baseValue * (plyPaperPrice + profitInRupees)) / 1000;
        
        wallPlyPrices.push({
          name: ply.name,
          type: ply.type,
          gsm: gsm,
          paperPrice: plyPaperPrice,
          baseValue: baseValue.toFixed(4),
          price: plyPrice.toFixed(2)
        });
        
        wallTotalPlyPrice += plyPrice;
      });

      // Weight Calculation: ((((Total GSM × Reel × Cutting) / 1550) × Joint) × Wastage) / 1000 = Weight in KG
      const baseWeight = (totalGSM * wallReel * wallCutting) / 1550;
      const weightWithJoint = baseWeight * wallJointMultiplier;
      const weightWithWastage = weightWithJoint * wastageMultiplier;
      const wallWeight = weightWithWastage / 1000; // in KG

      return {
        laminationCost: wallLaminationCost,
        plyPrices: wallPlyPrices,
        totalPlyPrice: wallTotalPlyPrice,
        weight: wallWeight,
        baseValue: baseWeight
      };
    };

    // Transport cost per unit: Transport Cost / MOQ (same for all)
    const transportCostPerUnit = moqVal > 0 ? transport / moqVal : 0;

    let finalBoxPrice = 0;
    let totalWeight = 0;
    let wall1Price = 0;
    let wall2Price = 0;
    let plyPrices = [];
    let totalPlyPrice = 0;
    let laminationCost = 0;
    let baseValue = 0;

    if (jointType === 'Four') {
      // Calculate Wall-1 (2 boards)
      const wall1 = calculateWallPrice(reel, cutting, 2);
      wall1Price = wall1.totalPlyPrice + (printing / 2) + wall1.laminationCost + (transportCostPerUnit / 2);
      
      // Calculate Wall-2 (2 boards)
      const wall2 = calculateWallPrice(reel2, cutting2, 2);
      wall2Price = wall2.totalPlyPrice + (printing / 2) + wall2.laminationCost + (transportCostPerUnit / 2);
      
      // Combine results + other costs
      finalBoxPrice = (wall1Price + wall2Price + totalOtherCosts) / upsValue;
      totalWeight = wall1.weight + wall2.weight;
      plyPrices = wall1.plyPrices; // Use Wall-1 ply prices for display
      totalPlyPrice = wall1.totalPlyPrice + wall2.totalPlyPrice;
      laminationCost = wall1.laminationCost + wall2.laminationCost;
      baseValue = wall1.baseValue + wall2.baseValue;
    } else {
      // Single or Double joint - use original calculation
      const wall1 = calculateWallPrice(reel, cutting, jointMultiplier);
      
      laminationCost = wall1.laminationCost;
      plyPrices = wall1.plyPrices;
      totalPlyPrice = wall1.totalPlyPrice;
      totalWeight = wall1.weight;
      baseValue = wall1.baseValue;
      
      // Final price = (Total Ply Price + Printing + Lamination + Transport + Other Costs) / Up's
      finalBoxPrice = (totalPlyPrice + printing + laminationCost + transportCostPerUnit + totalOtherCosts) / upsValue;
    }

    setCalculatedValues({
      totalGSM: totalGSM.toFixed(2),
      baseValue: baseValue.toFixed(4),
      laminationCost: laminationCost.toFixed(2),
      transportCostPerUnit: transportCostPerUnit.toFixed(2),
      profitMargin: margin.toFixed(2),
      jointMultiplier: jointType === 'Four' ? 4 : jointMultiplier,
      numberOfPartitions: numPartitions,
      plyPrices: plyPrices,
      totalPlyPrice: totalPlyPrice.toFixed(2),
      boxWeight: (totalWeight / upsValue).toFixed(3), // Weight per unit
      finalBoxPrice: finalBoxPrice.toFixed(2),
      // Four joint specific
      wall1Price: wall1Price.toFixed(2),
      wall2Price: wall2Price.toFixed(2),
      wall1ReelSize: reel,
      wall1CuttingSize: cutting,
      wall2ReelSize: reel2,
      wall2CuttingSize: cutting2,
      // New fields
      ups: upsValue,
      totalOtherCosts: totalOtherCosts.toFixed(2),
      otherCosts: otherCosts,
      wastagePercent: wastage
    });
  }, [plyConfig, reelSize, cuttingSize, jointType, numberOfPartitions, productType, profitMargin, profitMarginType, printingPrice, laminationEnabled, laminationVendorCost, transportCost, moq, wall2ReelSize, wall2CuttingSize, ups, otherCosts, wastagePercent, dimensionUnit]);

  // Auto-calculate when inputs change
  useEffect(() => {
    calculatePrice();
  }, [calculatePrice]);

  const generateQuotationNumber = () => {
    const nextNum = quotationCount + 1;
    return `QTCPI-${String(nextNum).padStart(4, '0')}`;
  };

  const handleSaveQuotation = async () => {
    if (!itemName || !selectedFormCompany) {
      toast.error('Please select company and item name');
      return;
    }

    // Validate Wall-2 when joint type is Four
    if (jointType === 'Four' && (!wall2ReelSize || !wall2CuttingSize)) {
      toast.error('Please enter Wall-2 Reel Size and Cutting Size for Four joint type');
      return;
    }

    setLoading(true);
    try {
      const quotationData = {
        quotation_type: 'individual',
        quotation_number: editingQuotation?.quotation_number || generateQuotationNumber(),
        product_type: productType, // Box, Layer, Partition, etc.
        item_name: itemName,
        box_name: itemName, // Keep for backward compatibility
        company_name: selectedFormCompany,
        production_type: productionType,
        dimensions: {
          length: parseFloat(dimensions.length) || 0,
          breadth: parseFloat(dimensions.breadth) || 0,
          height: parseFloat(dimensions.height) || 0,
          unit: dimensionUnit
        },
        reel_size: parseFloat(reelSize) || 0,
        cutting_size: parseFloat(cuttingSize) || 0,
        // Wall-2 for Four joint type
        wall2_reel_size: jointType === 'Four' ? (parseFloat(wall2ReelSize) || 0) : null,
        wall2_cutting_size: jointType === 'Four' ? (parseFloat(wall2CuttingSize) || 0) : null,
        joint_type: productType === 'Partition' ? 'Partition' : jointType,
        number_of_partitions: productType === 'Partition' ? parseInt(numberOfPartitions) || 1 : null,
        moq: parseFloat(moq) || 0,
        ply_count: parseInt(plyCount),
        ply_config: plyConfig,
        profit_margin: parseFloat(profitMargin) || 0,
        profit_margin_type: profitMarginType,
        printing_price: productType === 'Partition' ? 0 : parseFloat(printingPrice) || 0,
        lamination_enabled: laminationEnabled,
        lamination_vendor_cost: parseFloat(laminationVendorCost) || 0,
        transport_cost: parseFloat(transportCost) || 0,
        ups: parseInt(ups) || 1,
        other_costs: otherCosts,
        wastage_percent: parseFloat(wastagePercent) || 5,
        total_corrugated_rolls: parseInt(totalCorrugatedRolls) || 0,
        calculated_values: calculatedValues,
        status: 'in_process',
        comment: ''
      };

      if (editingQuotation) {
        await axios.put(`${API}/quotations/${editingQuotation.id}`, quotationData);
        toast.success('Quotation updated successfully!');
      } else {
        await axios.post(`${API}/quotations`, quotationData);
        toast.success('Quotation saved successfully!');
        setQuotationCount(prev => prev + 1);
      }
      
      // Set page company to the form company to show the new quotation
      setSelectedPageCompany(selectedFormCompany);
      fetchSavedQuotations();
      resetForm();
      setShowDialog(false);
      setEditingQuotation(null);
    } catch (error) {
      toast.error('Failed to save quotation');
      console.error('Error saving quotation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloneQuotation = async (quotation) => {
    try {
      const clonedData = {
        ...quotation,
        quotation_number: generateQuotationNumber(),
        status: 'in_process',
        created_at: undefined,
        id: undefined
      };
      delete clonedData.id;
      delete clonedData.created_at;
      
      await axios.post(`${API}/quotations`, clonedData);
      toast.success('Quotation cloned successfully!');
      setQuotationCount(prev => prev + 1);
      fetchSavedQuotations();
    } catch (error) {
      toast.error('Failed to clone quotation');
    }
  };

  const handleStatusChange = (quotationId, newStatus) => {
    if (newStatus === 'approved') {
      // Admin & master skip the password prompt entirely.
      if (bypassAllPasswords) {
        updateStatus(quotationId, newStatus);
        return;
      }
      // Require password for approval
      setPendingStatusChange({ quotationId, newStatus });
      setPasswordDialogOpen(true);
    } else {
      updateStatus(quotationId, newStatus);
    }
  };

  const confirmStatusChange = async () => {
    try {
      await axios.post(`${API}/auth/validate-page-password`, {
        password: passwordInput,
        type: "approval"
      });
      updateStatus(pendingStatusChange.quotationId, pendingStatusChange.newStatus);
      setPasswordDialogOpen(false);
      setPasswordInput('');
      setPendingStatusChange(null);
    } catch (err) {
      toast.error('Incorrect password');
    }
  };

  const updateStatus = async (quotationId, newStatus) => {
    try {
      await axios.put(`${API}/quotations/${quotationId}`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      
      // If approved, create a new entry in Prod & Materials Info
      if (newStatus === 'approved') {
        await createProductFromQuotation(quotationId);
      }
      
      fetchSavedQuotations();
      fetchProductSpecs(); // Refresh product specs in case new one was added
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const createProductFromQuotation = async (quotationId) => {
    try {
      // Find the quotation data
      const quotation = [...savedQuotations, ...approvedQuotations, ...allQuotations, ...allApprovedQuotations]
        .find(q => q.id === quotationId);
      
      if (!quotation) {
        console.error('Quotation not found for product creation');
        return;
      }
      
      // Build GSM config from ply_config
      const gsmConfig = (quotation.ply_config || []).map((config, index) => ({
        type: config.name || `Layer ${index + 1}`,
        gsm: config.gsm?.toString() || ''
      }));
      
      // Calculate rate from finalBoxPrice
      const rate = parseFloat(quotation.calculated_values?.finalBoxPrice || 0);
      
      // Create product specification data
      const productData = {
        company_name: quotation.company_name || '',
        item_name: quotation.item_name || quotation.box_name || '',
        length: quotation.dimensions?.length?.toString() || '',
        breadth: quotation.dimensions?.breadth?.toString() || '',
        height: quotation.dimensions?.height?.toString() || '',
        reel_size: quotation.reel_size?.toString() || quotation.wall1_reel_size?.toString() || '',
        cutting_size: quotation.cutting_size?.toString() || quotation.wall1_cutting_size?.toString() || '',
        ply: parseInt(quotation.ply_count) || 3,
        rate: rate,
        gsm_config: gsmConfig,
        // Additional fields from quotation
        joint_type: quotation.joint_type || '',
        production_type: quotation.production_type || '',
        quotation_number: quotation.quotation_number || ''
      };
      
      // Call API to create product specification
      const response = await axios.post(`${API}/product-specifications`, productData);
      toast.success(`Product ${response.data.product_id} created from quotation!`, {
        description: `${productData.item_name} added to Prod & Materials Info`
      });
    } catch (error) {
      console.error('Error creating product from quotation:', error);
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already exists')) {
        toast.info('Product already exists in Prod & Materials Info');
      } else {
        toast.error('Failed to create product from quotation');
      }
    }
  };

  const handleEditQuotation = (quotation) => {
    setEditingQuotation(quotation);
    // The loaded quotation already has reel/cutting set — block the
    // auto-recompute effect so it doesn't overwrite them as soon as the
    // user nudges a dimension. Same protection used for cloned quotations.
    reelManuallyEditedRef.current = true;
    // Tell the company-change effect to skip its "clear item fields" cleanup
    // for the first run after this load (effect resets the ref to false itself).
    loadingFromEditRef.current = true;
    setSelectedFormCompany(quotation.company_name || '');
    // Item name: prefer new `item_name`, fall back to legacy `box_name`.
    // ALSO restore the dropdown-side state: `selectedItem` controls the
    // combobox label and `useManualItemName` toggles between the searchable
    // dropdown and a free-text input. Without these the dropdown shows the
    // placeholder "Search from Prod & Materials..." even when item_name is set.
    const resolvedItemName = quotation.item_name || quotation.box_name || '';
    setItemName(resolvedItemName);
    setSelectedItem(resolvedItemName);
    // If item_name doesn't match a Product/Materials entry it's a manual entry —
    // flip to the free-text mode so the user sees the value populated.
    setUseManualItemName(!!resolvedItemName);
    setItemNameSearch('');
    // product_type: old quotations stored "3-Ply" / "5-Ply"; new ones store
    // "Box" / "Layer" / "Partition" / ... Coerce legacy ply markers to "Box"
    // so the dialog renders correctly. The actual ply count is captured by
    // ply_count below.
    const legacyPly = /^(3-Ply|5-Ply)$/i.test(quotation.product_type || '');
    setProductType(legacyPly ? 'Box' : (quotation.product_type || 'Box'));
    setProductionType(quotation.production_type || 'Manual Production');
    // Dimensions: legacy rows nested `ply` inside dimensions and omitted `unit`.
    const dims = quotation.dimensions || {};
    setDimensions({
      length: dims.length ?? '',
      breadth: dims.breadth ?? '',
      height: dims.height ?? '',
    });
    setDimensionUnit(dims.unit || 'mm');
    // Reel / cutting size can be number (new) or "24x14"-style string (legacy).
    setReelSize(quotation.reel_size != null ? String(quotation.reel_size) : '');
    setCuttingSize(quotation.cutting_size != null ? String(quotation.cutting_size) : '');
    // Wall-2 (Four joint type) — was missing entirely (data appeared "gone")
    setWall2ReelSize(quotation.wall2_reel_size != null ? String(quotation.wall2_reel_size) : '');
    setWall2CuttingSize(quotation.wall2_cutting_size != null ? String(quotation.wall2_cutting_size) : '');
    setJointType(quotation.joint_type || 'Single');
    setNumberOfPartitions(quotation.number_of_partitions?.toString() || '1');
    // MOQ: new field is `moq`, legacy field was `quantity`.
    setMoq(
      quotation.moq != null ? String(quotation.moq)
        : quotation.quantity != null ? String(quotation.quantity)
        : ''
    );
    // Ply count: new field is `ply_count`, legacy nested it inside `dimensions.ply`.
    setPlyCount(
      quotation.ply_count != null ? String(quotation.ply_count)
        : dims.ply != null ? String(dims.ply)
        : '3'
    );
    setPlyConfig(quotation.ply_config || []);
    setProfitMargin(quotation.profit_margin != null ? String(quotation.profit_margin) : '30');
    setProfitMarginType(quotation.profit_margin_type || 'rupees');
    setPrintingPrice(quotation.printing_price != null ? String(quotation.printing_price) : '0');
    setLaminationEnabled(quotation.lamination_enabled || false);
    setLaminationVendorCost(quotation.lamination_vendor_cost != null ? String(quotation.lamination_vendor_cost) : '');
    setTransportCost(quotation.transport_cost != null ? String(quotation.transport_cost) : '');
    setUps(quotation.ups != null ? String(quotation.ups) : '1');
    // other_costs rows need a stable React _uid (Feb 2026 fix); back-fill on load.
    const ocs = (quotation.other_costs || []).map(c => ({
      ...c,
      _uid: c._uid || (typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `cost-${Date.now()}-${Math.random().toString(36).slice(2,8)}`),
    }));
    setOtherCosts(ocs);
    setWastagePercent(quotation.wastage_percent != null ? String(quotation.wastage_percent) : '5');
    setTotalCorrugatedRolls(quotation.total_corrugated_rolls != null ? String(quotation.total_corrugated_rolls) : '');
    setShowDialog(true);
  };

  const handleViewQuotation = (quotation) => {
    setViewingQuotation(quotation);
    setShowViewDialog(true);
  };

  const handleAddComment = async () => {
    if (!commentingQuotationId) return;
    
    try {
      await axios.put(`${API}/quotations/${commentingQuotationId}`, { comment: currentComment });
      toast.success('Comment saved!');
      setCommentDialogOpen(false);
      setCurrentComment('');
      setCommentingQuotationId(null);
      fetchSavedQuotations();
    } catch (error) {
      toast.error('Failed to save comment');
    }
  };

  const openCommentDialog = (quotation) => {
    setCommentingQuotationId(quotation.id);
    setCurrentComment(quotation.comment || '');
    setCommentDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedFormCompany('');
    setSelectedItem('');
    setItemName('');
    setUseManualItemName(false);
    setItemNameSearch('');
    setProductType('Box');
    setProductionType('Manual Production');
    setDimensions({ length: '', breadth: '', height: '' });
    setDimensionUnit('mm');
    setReelSize('');
    setCuttingSize('');
    setWall2ReelSize('');
    setWall2CuttingSize('');
    setJointType('Single');
    setNumberOfPartitions('1');
    setMoq('');
    setPlyCount('3');
    setProfitMargin('30');
    setProfitMarginType('rupees');
    setPrintingPrice('0');
    setLaminationEnabled(false);
    setLaminationVendorCost('');
    setTransportCost('');
    setUps('1');
    setOtherCosts([]);
    setWastagePercent('5');
    setTotalCorrugatedRolls('');
    setEditingQuotation(null);
    // Fresh quotation → allow first-time auto-compute of Reel/Cutting from
    // dimensions until the user manually edits them.
    reelManuallyEditedRef.current = false;
  };

  const deleteQuotation = async (id) => {
    try {
      await axios.delete(`${API}/quotations/${id}`);
      toast.success('Quotation moved to archive (will be deleted after 30 days)');
      fetchSavedQuotations();
    } catch (error) {
      toast.error('Failed to delete quotation');
    }
  };

  const restoreQuotation = async (id) => {
    try {
      await axios.post(`${API}/quotations/${id}/restore`);
      toast.success('Quotation restored');
      fetchSavedQuotations();
    } catch (error) {
      toast.error('Failed to restore quotation');
    }
  };

  const permanentDeleteQuotation = async (id) => {
    try {
      await axios.delete(`${API}/quotations/${id}/permanent`);
      toast.success('Quotation permanently deleted');
      fetchSavedQuotations();
    } catch (error) {
      toast.error('Failed to delete quotation');
    }
  };

  const openQuotationDialog = () => {
    resetForm();
    // Pre-fill form company with page company if selected
    if (selectedPageCompany) {
      setSelectedFormCompany(selectedPageCompany);
    }
    setShowDialog(true);
  };

  // Print Proposal - styled like PO form
  const printProposal = (quotation) => {
    // Use the public logo path
    const logoUrl = `${window.location.origin}/logo.png`;
    const htmlContent = `
      <html>
        <head>
          <title>Proposal - ${quotation.quotation_number || 'QTCPI-XXXX'}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
            body { font-family: Arial, sans-serif; padding: 20px; color: #333; background: #fff; }
            .container { max-width: 800px; margin: 0 auto; border: 2px solid #1a365d; }
            
            /* Header - Like PO Form */
            .header { background: #1a365d !important; color: white !important; padding: 20px; display: flex; justify-content: space-between; align-items: flex-start; }
            .logo-section { display: flex; align-items: center; gap: 15px; }
            .logo { width: 70px; height: 70px; background: white; border-radius: 8px; padding: 5px; }
            .company-info h1 { font-size: 18px; color: white !important; margin-bottom: 3px; font-weight: bold; }
            .company-info p { font-size: 10px; color: white !important; opacity: 0.9; line-height: 1.4; }
            .doc-info { text-align: right; }
            .doc-info h2 { font-size: 22px; color: #ffd700 !important; margin-bottom: 8px; letter-spacing: 1px; }
            .doc-info .doc-number { font-size: 14px; font-weight: bold; color: white !important; background: rgba(255,255,255,0.2) !important; padding: 5px 12px; border-radius: 3px; display: inline-block; margin-bottom: 5px; }
            .doc-info .doc-date { font-size: 11px; color: white !important; opacity: 0.9; }
            
            /* To Section - Like PO */
            .to-section { background: #f0f7ff !important; padding: 15px; border-bottom: 2px solid #1a365d; }
            .to-section h3 { font-size: 11px; color: #1a365d !important; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 1px; font-weight: bold; }
            .to-section .company-name { font-size: 16px; font-weight: bold; color: #1a365d !important; }
            .to-section .gstin { font-size: 10px; color: #555 !important; margin-top: 3px; }
            
            /* Item Table - Like PO */
            .item-table { width: 100%; border-collapse: collapse; }
            .item-table th { background: #1a365d !important; color: white !important; padding: 12px 8px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
            .item-table td { padding: 12px 8px; border-bottom: 1px solid #e2e8f0; font-size: 11px; vertical-align: top; }
            .item-table tr:nth-child(even) { background: #f8fafc !important; }
            .item-table .item-name { font-weight: 600; color: #1a365d !important; font-size: 12px; }
            .item-table .item-specs { color: #555 !important; font-size: 10px; margin-top: 4px; line-height: 1.5; }
            .item-table .text-right { text-align: right; }
            .item-table .hsn { color: #666 !important; font-family: monospace; }
            
            /* Specs Grid */
            .specs-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 5px; margin-top: 8px; }
            .spec-item { font-size: 10px; color: #555 !important; }
            .spec-label { color: #888 !important; }
            
            /* Totals - Like PO */
            .totals-section { padding: 15px 20px; background: #f8fafc !important; }
            .totals-row { display: flex; justify-content: flex-end; margin-bottom: 8px; }
            .totals-label { width: 150px; text-align: right; padding-right: 20px; font-size: 12px; color: #555 !important; }
            .totals-value { width: 120px; text-align: right; font-size: 12px; font-weight: 500; }
            .grand-total { background: #1a365d !important; color: white !important; padding: 12px 20px; margin: 0 -20px; }
            .grand-total .totals-label, .grand-total .totals-value { color: white !important; font-size: 14px; font-weight: bold !important; }
            
            /* Amount in Words */
            .amount-words { background: #e6f0ff !important; padding: 12px 15px; border-top: 2px solid #1a365d; }
            .amount-words strong { color: #1a365d !important; font-size: 11px; }
            .amount-words span { font-size: 11px; color: #333 !important; }
            
            /* Terms */
            .terms { padding: 20px; border-top: 1px solid #e2e8f0; }
            .terms h3 { font-size: 12px; color: #1a365d !important; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
            .terms ul { font-size: 10px; color: #555 !important; padding-left: 18px; line-height: 1.8; }
            .terms li { margin-bottom: 3px; }
            
            /* Signature */
            .signature { padding: 30px 20px 20px; text-align: right; border-top: 1px solid #e2e8f0; }
            .signature-line { border-top: 2px solid #1a365d; width: 200px; margin-left: auto; padding-top: 8px; font-size: 11px; color: #1a365d !important; font-weight: 600; }
            
            /* Footer */
            .footer { background: #1a365d !important; color: white !important; text-align: center; font-size: 9px; padding: 10px; }
            
            @media print {
              body { padding: 0; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              .container { max-width: 100%; border: none; }
              .header { background: #1a365d !important; -webkit-print-color-adjust: exact !important; }
              .item-table th { background: #1a365d !important; -webkit-print-color-adjust: exact !important; }
              .grand-total { background: #1a365d !important; -webkit-print-color-adjust: exact !important; }
              .footer { background: #1a365d !important; -webkit-print-color-adjust: exact !important; }
              .to-section { background: #f0f7ff !important; -webkit-print-color-adjust: exact !important; }
              .amount-words { background: #e6f0ff !important; -webkit-print-color-adjust: exact !important; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <div class="logo-section">
                <img src="${logoUrl}" class="logo" onerror="this.style.display='none'" />
                <div class="company-info">
                  <h1>Challenge Packaging Industries</h1>
                  <p>Tamil Nadu, India<br/>
                  Phone: 7299808164<br/>
                  Email: info@challengepack.in<br/>
                  GSTIN: 33AGSPV7578C2ZP</p>
                </div>
              </div>
              <div class="doc-info">
                <h2>QUOTATION</h2>
                <div class="doc-number">${quotation.quotation_number || 'QTCPI-XXXX'}</div>
                <div class="doc-date">Date: ${formatDate(quotation.created_at)}</div>
              </div>
            </div>

            <div class="to-section">
              <h3>To</h3>
              <div class="company-name">${quotation.company_name || '-'}</div>
              <div class="gstin">Place of Supply: Tamil Nadu (33)</div>
            </div>

            <table class="item-table">
              <thead>
                <tr>
                  <th style="width: 5%">#</th>
                  <th style="width: 50%">Item & Description</th>
                  <th style="width: 12%">HSN/SAC</th>
                  <th style="width: 10%">Qty</th>
                  <th style="width: 13%" class="text-right">Rate</th>
                  <th style="width: 10%" class="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td>
                    <div class="item-name">${quotation.item_name || quotation.box_name}</div>
                    <div class="item-specs">
                      <div class="specs-grid">
                        <div class="spec-item"><span class="spec-label">Ply:</span> ${quotation.ply_count} Ply</div>
                        <div class="spec-item"><span class="spec-label">Production:</span> ${quotation.production_type || 'Manual'}</div>
                        <div class="spec-item"><span class="spec-label">Dimensions:</span> ${quotation.dimensions?.length || 0} × ${quotation.dimensions?.breadth || 0} × ${quotation.dimensions?.height || 0} ${quotation.dimensions?.unit || 'mm'}</div>
                        <div class="spec-item"><span class="spec-label">MOQ:</span> ${quotation.moq || 1}</div>
                      </div>
                      <div style="margin-top: 8px; font-size: 9px; color: #777;">
                        <strong>Ply Config:</strong> ${quotation.ply_config?.map(p => `${p.name}: ${p.shade || '-'} ${p.gsm || 0}GSM`).join(' | ') || '-'}
                      </div>
                    </div>
                  </td>
                  <td class="hsn">4819</td>
                  <td>${quotation.moq || 1}</td>
                  <td class="text-right">₹${quotation.calculated_values?.finalBoxPrice || '0.00'}</td>
                  <td class="text-right">₹${(parseFloat(quotation.calculated_values?.finalBoxPrice || 0) * (quotation.moq || 1)).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>

            <div class="totals-section">
              <div class="totals-row">
                <div class="totals-label">Sub Total</div>
                <div class="totals-value">₹${(parseFloat(quotation.calculated_values?.finalBoxPrice || 0) * (quotation.moq || 1)).toFixed(2)}</div>
              </div>
              <div class="totals-row grand-total">
                <div class="totals-label">Grand Total</div>
                <div class="totals-value">₹${(parseFloat(quotation.calculated_values?.finalBoxPrice || 0) * (quotation.moq || 1)).toFixed(2)}</div>
              </div>
            </div>

            <div class="amount-words">
              <strong>Total In Words: </strong>
              <span>Indian Rupees ${numberToWords(parseFloat(quotation.calculated_values?.finalBoxPrice || 0) * (quotation.moq || 1))} Only</span>
            </div>

            <div class="terms">
              <h3>Terms & Conditions</h3>
              <ul>
                <li>Quotation is valid for 30 days from the date of issue.</li>
                <li>Prices are inclusive of taxes.</li>
                <li>Report defects within 7 days for replacement.</li>
                <li>Die and Stereo cost will be extra.</li>
              </ul>
            </div>

            ${quotation.comment ? `
              <div class="terms">
                <h3>Notes</h3>
                <p style="font-size: 11px; color: #555;">${quotation.comment}</p>
              </div>
            ` : ''}

            <div class="signature">
              <div class="signature-line">Authorized Signature</div>
            </div>

            <div class="footer">
              Challenge Packaging Industries | This is a computer-generated quotation
            </div>
          </div>
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `;
    openPrintWindow(htmlContent);
  };

  // Print Table
  const printTable = (tableName) => {
    const quotations = tableName.includes('Approved') ? approvedQuotations : savedQuotations;
    const tableHtmlContent = `
      <html>
        <head>
          <title>${tableName}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
            th { background-color: #1a1a2e; color: white; }
            h1 { text-align: center; color: #333; margin-bottom: 5px; }
            .subtitle { text-align: center; color: #666; margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <h1>${tableName}</h1>
          <p class="subtitle">Company: ${selectedPageCompany} | Generated: ${new Date().toLocaleDateString()}</p>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Quote #</th>
                <th>Item Name</th>
                <th>Ply</th>
                <th>Price</th>
                <th>Status</th>
                <th>Comment</th>
              </tr>
            </thead>
            <tbody>
              ${quotations.map(q => `
                <tr>
                  <td>${formatDate(q.created_at)}</td>
                  <td>${q.quotation_number || '-'}</td>
                  <td>${q.item_name || q.box_name}</td>
                  <td>${q.ply_count} Ply</td>
                  <td>₹${q.calculated_values?.finalBoxPrice || '0.00'}</td>
                  <td>${q.status || 'In Process'}</td>
                  <td>${q.comment || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `;
    openPrintWindow(tableHtmlContent);
  };

  // Helper function to convert number to words
  const numberToWords = (num) => {
    if (num === 0) return 'Zero';
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    const numToWords = (n) => {
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + numToWords(n % 100) : '');
      if (n < 100000) return numToWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + numToWords(n % 1000) : '');
      if (n < 10000000) return numToWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + numToWords(n % 100000) : '');
      return numToWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + numToWords(n % 10000000) : '');
    };
    
    const rupees = Math.floor(num);
    const paise = Math.round((num - rupees) * 100);
    
    let result = numToWords(rupees);
    if (paise > 0) {
      result += ' and ' + numToWords(paise) + ' Paise';
    }
    return result;
  };

  const getPlyStructureDisplay = () => {
    if (plyConfig.length === 0) return '';
    return plyConfig.map((p) => {
      const gsm = p.gsm || '0';
      const multiplier = p.type === 'flute' ? ' × 150%' : '';
      return `${p.name} (${gsm} GSM${multiplier})`;
    }).join(' + ');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Handle Create Quotation dialog print
  const handleCreateQuotationPrint = () => {
    if (!selectedQuotationForPrint) {
      toast.error('Please select an item to print');
      return;
    }
    const quotation = availableQuotationsForCompany.find(q => q.id === selectedQuotationForPrint);
    if (quotation) {
      printProposal(quotation);
      setShowCreateQuotationDialog(false);
      setCreateQuotationCompany('');
      setSelectedQuotationForPrint('');
    }
  };

  // Quotation Table Component
  const QuotationTable = ({ quotations, tableName, isApproved = false, showCompany = false }) => (
    <Table>
      <TableHeader>
        <TableRow className="bg-gray-800/50 border-gray-700">
          <TableHead className="text-gray-200">Date</TableHead>
          <TableHead className="text-gray-200">Quote #</TableHead>
          {showCompany && <TableHead className="text-gray-200">Company</TableHead>}
          <TableHead className="text-gray-200">Item Name</TableHead>
          <TableHead className="text-gray-200">Ply</TableHead>
          <TableHead className="text-gray-200">Price</TableHead>
          <TableHead className="text-gray-200">Status</TableHead>
          <TableHead className="text-gray-200">View</TableHead>
          <TableHead className="text-gray-200">Edit</TableHead>
          <TableHead className="text-gray-200">Comment</TableHead>
          {!isApproved && <TableHead className="text-gray-200">Clone</TableHead>}
          <TableHead className="text-gray-200">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {quotations.length === 0 ? (
          <TableRow>
            <TableCell colSpan={isApproved ? (showCompany ? 11 : 10) : (showCompany ? 12 : 11)} className="text-center py-8 text-gray-400">
              No quotations found
            </TableCell>
          </TableRow>
        ) : (
          quotations.map((q) => (
            <TableRow key={q.id} className="border-gray-700 hover:bg-gray-800/30">
              <TableCell className="text-gray-300">{formatDate(q.created_at)}</TableCell>
              <TableCell className="text-cyan-400 font-mono text-sm">{q.quotation_number || '-'}</TableCell>
              {showCompany && <TableCell className="text-yellow-400 font-medium">{q.company_name || '-'}</TableCell>}
              <TableCell className="text-white font-medium">{q.item_name || q.box_name}</TableCell>
              <TableCell>
                <span className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded text-sm">
                  {q.ply_count} Ply
                </span>
              </TableCell>
              <TableCell className="text-green-400 font-bold">₹{q.calculated_values?.finalBoxPrice || '0.00'}</TableCell>
              <TableCell>
                {/* Status dropdown - available for both In Process and Approved tables */}
                <Select 
                  value={q.status || 'in_process'} 
                  onValueChange={(v) => handleStatusChange(q.id, v)}
                >
                  <SelectTrigger className={`w-32 h-8 bg-gray-800 border-gray-600 text-white text-sm ${isApproved ? 'border-green-500/30' : ''}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_process">In Process</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">
                      <span className="flex items-center gap-1">
                        <Lock className="h-3 w-3" /> Approved
                      </span>
                    </SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                <Button variant="ghost" size="sm" onClick={() => handleViewQuotation(q)} className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10">
                  <Eye className="h-4 w-4" />
                </Button>
              </TableCell>
              <TableCell>
                <Button variant="ghost" size="sm" onClick={() => handleEditQuotation(q)} className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10">
                  <Edit className="h-4 w-4" />
                </Button>
              </TableCell>
              <TableCell>
                <Button variant="ghost" size="sm" onClick={() => openCommentDialog(q)} className="text-gray-400 hover:text-yellow-400 hover:bg-yellow-500/10">
                  <MessageSquare className="h-4 w-4" />
                </Button>
              </TableCell>
              {!isApproved && (
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => handleCloneQuotation(q)} className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10" title="Clone Quotation">
                    <Copy className="h-4 w-4" />
                  </Button>
                </TableCell>
              )}
              <TableCell>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => printProposal(q)} className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10" title="Print Proposal">
                    <Printer className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteQuotation(q.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  // Render the Sent Quotations log table — every send is a row, newest first.
  // Optional `companyFilter`: pass the currently-selected page company to
  // filter; pass null/undefined to show all companies.
  const SentLogTable = ({ companyFilter }) => {
    const rows = companyFilter
      ? sentLog.filter(r => (r.customer_name || '').toLowerCase() === companyFilter.toLowerCase())
      : sentLog;
    const fmtDate = (iso) => {
      try {
        const d = new Date(iso);
        return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
      } catch { return iso; }
    };
    if (rows.length === 0) {
      return (
        <div className="text-center py-10 text-gray-400">
          <Mail className="h-10 w-10 mx-auto mb-2 opacity-40" />
          <p>No sent quotations yet{companyFilter ? ` for ${companyFilter}` : ''}.</p>
          <p className="text-xs text-gray-500 mt-1">Successful emails sent from "Create Quotation → Send Email" appear here.</p>
        </div>
      );
    }
    return (
      <Table>
        <TableHeader>
          <TableRow className="border-gray-700 hover:bg-gray-800/50">
            <TableHead className="text-gray-200">Sent At</TableHead>
            <TableHead className="text-gray-200">QT #</TableHead>
            {!companyFilter && <TableHead className="text-gray-200">Customer</TableHead>}
            <TableHead className="text-gray-200">To</TableHead>
            <TableHead className="text-gray-200">CC</TableHead>
            <TableHead className="text-gray-200">BCC</TableHead>
            <TableHead className="text-gray-200">Amount</TableHead>
            <TableHead className="text-gray-200">Sender</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} className="border-gray-800 hover:bg-gray-800/30">
              <TableCell className="text-gray-200 text-xs">{fmtDate(r.sent_at)}</TableCell>
              <TableCell className="text-cyan-400 font-mono text-xs">{r.quotation_number}</TableCell>
              {!companyFilter && <TableCell className="text-gray-200 text-xs">{r.customer_name}</TableCell>}
              <TableCell className="text-gray-200 text-xs">{r.to_email}</TableCell>
              <TableCell className="text-gray-400 text-xs">{(r.cc || []).join(', ') || '—'}</TableCell>
              <TableCell className="text-gray-400 text-xs">{(r.bcc || []).join(', ') || '—'}</TableCell>
              <TableCell className="text-green-400 text-xs">₹{Number(r.total_amount || 0).toFixed(2)}</TableCell>
              <TableCell className="text-gray-300 text-xs">{(r.sender_name || '').split(' - ')[0]}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  // Check page authentication via backend
  const handlePageAuth = async () => {
    try {
      await axios.post(`${API}/auth/validate-page-password`, {
        password: pagePasswordInput,
        type: "page"
      });
      setIsAuthenticated(true);
      setPagePasswordError('');
    } catch (err) {
      setPagePasswordError('Incorrect password');
    }
  };

  // While the session probe is in flight, render a tiny spinner instead of
  // either the locked Card or the full page. Prevents the password dialog
  // from flashing on every admin page load.
  if (authChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950" data-testid="quotation-auth-checking">
        <div className="h-10 w-10 rounded-full border-2 border-amber-400/40 border-t-amber-400 animate-spin" />
      </div>
    );
  }

  // If not authenticated, show password dialog
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <Card className="w-full max-w-md bg-gray-900/80 border-amber-500/30">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mb-4">
              <Lock className="h-8 w-8 text-amber-400" />
            </div>
            <CardTitle className="text-2xl text-amber-400">Quotation Access</CardTitle>
            <p className="text-gray-400 mt-2">Enter password to access the quotation calculator</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-gray-300">Password</Label>
              <PasswordInput 
                value={pagePasswordInput} 
                onChange={(e) => setPagePasswordInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handlePageAuth()}
                placeholder="Enter password..." 
                className="bg-gray-800 border-gray-600 text-white mt-1"
                data-testid="page-password-input"
              />
              {pagePasswordError && <p className="text-red-400 text-sm mt-1">{pagePasswordError}</p>}
            </div>
            <Button 
              onClick={handlePageAuth} 
              className="w-full bg-amber-600 hover:bg-amber-500 text-white"
              data-testid="page-password-submit"
            >
              Access Quotations
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button onClick={() => navigate('/workorder')} variant="ghost" className="text-gray-400 hover:text-white" data-testid="back-to-workorder">
            <ArrowLeft className="h-5 w-5 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Calculator className="h-8 w-8 text-cyan-400" />
              Box Quotation Calculator
            </h1>
            <p className="text-gray-400 mt-1">Calculate and manage box pricing proposals</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button 
            variant={showAllQuotations ? "default" : "outline"}
            onClick={() => {
              setShowAllQuotations(!showAllQuotations);
              if (!showAllQuotations) {
                fetchAllQuotations();
              }
            }}
            className={showAllQuotations ? "bg-purple-600 text-white" : "border-purple-500/50 text-purple-400 hover:bg-purple-500/10"}
            data-testid="view-all-quotations-btn"
          >
            <Eye className="h-5 w-5 mr-2" />
            {showAllQuotations ? 'Show By Company' : 'View All Quotations'}
          </Button>
          <Button 
            variant={showArchived ? "default" : "outline"}
            onClick={() => {
              setShowArchived(!showArchived);
              // Refresh archived data when showing
              if (!showArchived) {
                fetchAllQuotations();
              }
            }}
            className={showArchived ? "bg-gray-600 text-white" : "border-gray-600 text-gray-300"}
          >
            <Archive className="h-5 w-5 mr-2" />
            {showArchived ? 'Hide Archive' : 'Show Archive'} ({showAllQuotations ? allArchivedQuotations.length : archivedQuotations.length})
          </Button>
          <Button 
            onClick={() => navigate('/workorder/quotation/create')}
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-semibold px-6 py-3 rounded-lg shadow-lg"
            data-testid="create-quotation-btn"
          >
            <FileText className="h-5 w-5 mr-2" />
            Create Quotation
          </Button>
          <Button 
            onClick={openQuotationDialog}
            className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold px-6 py-3 rounded-lg shadow-lg"
            data-testid="new-quotation-btn"
          >
            <Plus className="h-5 w-5 mr-2" />
            Calculation
          </Button>
        </div>
      </div>

      {/* Company Selection for Page - hide when viewing all */}
      {!showAllQuotations && (
        <Card className="bg-gray-900/50 border-gray-700">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Label className="text-gray-300 text-lg whitespace-nowrap">Select Company:</Label>
              <Popover open={pageCompanyOpen} onOpenChange={setPageCompanyOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" aria-expanded={pageCompanyOpen} className="w-full max-w-md justify-between bg-gray-800 border-gray-600 text-white">
                    {selectedPageCompany || "Select company to view quotations..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full max-w-md p-0 bg-gray-900 border-gray-700">
                  <Command className="bg-gray-900">
                    <CommandInput placeholder="Search company..." className="text-white" />
                    <CommandList>
                      <CommandEmpty>No company found.</CommandEmpty>
                      <CommandGroup>
                        {customers.map((customer) => (
                          <CommandItem key={customer.id} value={customer.company_name} onSelect={(value) => { setSelectedPageCompany(value); setPageCompanyOpen(false); }} className="text-white hover:bg-gray-800">
                            <Check className={`mr-2 h-4 w-4 ${selectedPageCompany === customer.company_name ? "opacity-100" : "opacity-0"}`} />
                            {customer.company_name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </CardContent>
        </Card>
      )}

      {/* View All Quotations Mode */}
      {showAllQuotations ? (
        <>
        <Tabs defaultValue="in_process" className="w-full">
          <TabsList className="bg-gray-800 border border-gray-700">
            <TabsTrigger value="in_process" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              <Clock className="h-4 w-4 mr-2" />
              All In Process ({allQuotations.length})
            </TabsTrigger>
            <TabsTrigger value="approved" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
              <CheckCircle className="h-4 w-4 mr-2" />
              All Approved ({allApprovedQuotations.length})
            </TabsTrigger>
            <TabsTrigger value="sent" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="all-sent-tab">
              <Mail className="h-4 w-4 mr-2" />
              All Sent ({sentLog.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="in_process" className="mt-4">
            <Card className="bg-gray-900/80 border-purple-500/30">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-xl text-purple-400 flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  All In Process Proposals
                </CardTitle>
                <Button onClick={() => printTable('All In Process Proposals')} variant="outline" className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10">
                  <Printer className="h-4 w-4 mr-2" />
                  Print Table
                </Button>
              </CardHeader>
              <CardContent>
                <QuotationTable quotations={allQuotations} tableName="All In Process Proposals" showCompany={true} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="approved" className="mt-4">
            <Card className="bg-gray-900/80 border-green-500/30">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-xl text-green-400 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  All Approved Proposals
                </CardTitle>
                <Button onClick={() => printTable('All Approved Proposals')} variant="outline" className="border-green-500/50 text-green-400 hover:bg-green-500/10">
                  <Printer className="h-4 w-4 mr-2" />
                  Print Table
                </Button>
              </CardHeader>
              <CardContent>
                <QuotationTable quotations={allApprovedQuotations} tableName="All Approved Proposals" isApproved={true} showCompany={true} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sent" className="mt-4">
            <Card className="bg-gray-900/80 border-blue-500/30">
              <CardHeader>
                <CardTitle className="text-xl text-blue-400 flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  All Sent Quotations
                </CardTitle>
                <p className="text-xs text-gray-500">Full log — every email send creates a new row; same quotation can appear multiple times.</p>
              </CardHeader>
              <CardContent>
                <SentLogTable companyFilter={null} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Archived Quotations in View All mode */}
        {showArchived && allArchivedQuotations.length > 0 && (
          <Card className="bg-gray-900/50 border-2 border-gray-600 mt-6">
            <CardHeader className="bg-gray-800/50 border-b border-gray-700">
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg text-gray-300 flex items-center gap-2">
                  <Archive className="h-5 w-5 text-gray-400" />
                  All Archived Quotations ({allArchivedQuotations.length})
                </CardTitle>
                <p className="text-xs text-gray-500">Auto-deleted after 30 days</p>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-700 hover:bg-gray-800/50">
                    <TableHead className="text-gray-200">QT #</TableHead>
                    <TableHead className="text-gray-200">Company</TableHead>
                    <TableHead className="text-gray-200">Item Name</TableHead>
                    <TableHead className="text-gray-200">Price</TableHead>
                    <TableHead className="text-gray-200">Deleted On</TableHead>
                    <TableHead className="text-gray-200">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allArchivedQuotations.map((q) => (
                    <TableRow key={q.id} className="border-gray-700 hover:bg-gray-800/30">
                      <TableCell className="text-cyan-400 font-mono">{q.quotation_number || '-'}</TableCell>
                      <TableCell className="text-yellow-400">{q.company_name || '-'}</TableCell>
                      <TableCell className="text-white">{q.item_name || q.box_name}</TableCell>
                      <TableCell className="text-green-400 font-bold">₹{q.calculated_values?.finalBoxPrice || '0'}</TableCell>
                      <TableCell className="text-gray-400">{q.deleted_at ? new Date(q.deleted_at).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => restoreQuotation(q.id)}
                            className="h-8 border-green-500/50 text-green-400 hover:bg-green-500/10"
                          >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Restore
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => permanentDeleteQuotation(q.id)}
                            className="h-8"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </>
      ) : selectedPageCompany ? (
        <>
        <Tabs defaultValue="in_process" className="w-full">
          <TabsList className="bg-gray-800 border border-gray-700">
            <TabsTrigger value="in_process" className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white">
              <Clock className="h-4 w-4 mr-2" />
              In Process ({savedQuotations.length})
            </TabsTrigger>
            <TabsTrigger value="approved" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
              <CheckCircle className="h-4 w-4 mr-2" />
              Approved ({approvedQuotations.length})
            </TabsTrigger>
            <TabsTrigger value="sent" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="company-sent-tab">
              <Mail className="h-4 w-4 mr-2" />
              Sent ({sentLog.filter(r => (r.customer_name || '').toLowerCase() === (selectedPageCompany || '').toLowerCase()).length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="in_process" className="mt-4">
            <Card className="bg-gray-900/80 border-gray-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-xl text-cyan-400 flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  In Process Proposals - {selectedPageCompany}
                </CardTitle>
                <Button onClick={() => printTable(`In Process Proposals - ${selectedPageCompany}`)} variant="outline" className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10">
                  <Printer className="h-4 w-4 mr-2" />
                  Print Table
                </Button>
              </CardHeader>
              <CardContent>
                <QuotationTable quotations={savedQuotations} tableName="In Process Proposals" />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="approved" className="mt-4">
            <Card className="bg-gray-900/80 border-gray-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-xl text-green-400 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  Approved Proposals - {selectedPageCompany}
                </CardTitle>
                <Button onClick={() => printTable(`Approved Proposals - ${selectedPageCompany}`)} variant="outline" className="border-green-500/50 text-green-400 hover:bg-green-500/10">
                  <Printer className="h-4 w-4 mr-2" />
                  Print Table
                </Button>
              </CardHeader>
              <CardContent>
                <QuotationTable quotations={approvedQuotations} tableName="Approved Proposals" isApproved={true} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sent" className="mt-4">
            <Card className="bg-gray-900/80 border-blue-500/30">
              <CardHeader>
                <CardTitle className="text-xl text-blue-400 flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Sent Quotations - {selectedPageCompany}
                </CardTitle>
                <p className="text-xs text-gray-500">Every email send creates a new row.</p>
              </CardHeader>
              <CardContent>
                <SentLogTable companyFilter={selectedPageCompany} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Archived Quotations Section */}
        {showArchived && archivedQuotations.length > 0 && (
          <Card className="bg-gray-900/50 border-2 border-gray-600 mt-6">
            <CardHeader className="bg-gray-800/50 border-b border-gray-700">
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg text-gray-300 flex items-center gap-2">
                  <Archive className="h-5 w-5 text-gray-400" />
                  Archived Quotations - {selectedPageCompany} ({archivedQuotations.length})
                </CardTitle>
                <p className="text-xs text-gray-500">Auto-deleted after 30 days</p>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-700 hover:bg-gray-800/50">
                    <TableHead className="text-gray-200">QT #</TableHead>
                    <TableHead className="text-gray-200">Type</TableHead>
                    <TableHead className="text-gray-200">Item Name</TableHead>
                    <TableHead className="text-gray-200">Price</TableHead>
                    <TableHead className="text-gray-200">Deleted On</TableHead>
                    <TableHead className="text-gray-200">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {archivedQuotations.map((q) => (
                    <TableRow key={q.id} className="border-gray-700 hover:bg-gray-800/30">
                      <TableCell className="text-cyan-400 font-mono">{q.quotation_number}</TableCell>
                      <TableCell className="text-gray-300">{q.product_type || 'Box'}</TableCell>
                      <TableCell className="text-white">{q.item_name || q.box_name}</TableCell>
                      <TableCell className="text-green-400 font-bold">₹{q.calculated_values?.finalBoxPrice || '0'}</TableCell>
                      <TableCell className="text-gray-400">{q.deleted_at ? new Date(q.deleted_at).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => restoreQuotation(q.id)}
                            className="h-8 border-green-500/50 text-green-400 hover:bg-green-500/10"
                          >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Restore
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => permanentDeleteQuotation(q.id)}
                            className="h-8"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </>
      ) : (
        <Card className="bg-gray-900/50 border-gray-700">
          <CardContent className="py-16 text-center">
            <Building2 className="h-16 w-16 mx-auto text-gray-600 mb-4" />
            <h3 className="text-xl text-gray-400">Select a Company or View All</h3>
            <p className="text-gray-500 mt-2">Choose a company from the dropdown above or click "View All Quotations" to see all proposals</p>
          </CardContent>
        </Card>
      )}

      {/* Create Quotation Dialog */}
      <Dialog open={showCreateQuotationDialog} onOpenChange={setShowCreateQuotationDialog}>
        <DialogContent className="sm:max-w-lg bg-gray-950 border-2 border-green-500/30">
          <DialogHeader>
            <DialogTitle className="text-xl text-green-400 flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Create Quotation
            </DialogTitle>
            <DialogDescription className="text-gray-400">Select a company and item to generate a quotation proposal</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Company Selection */}
            <div>
              <Label className="text-gray-300">Company Name *</Label>
              <Popover open={createQuotationCompanyOpen} onOpenChange={setCreateQuotationCompanyOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between bg-gray-800 border-gray-600 text-white mt-1">
                    {createQuotationCompany || "Select company..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0 bg-gray-900 border-gray-700">
                  <Command className="bg-gray-900">
                    <CommandInput placeholder="Search company..." className="text-white" />
                    <CommandList>
                      <CommandEmpty>No company found.</CommandEmpty>
                      <CommandGroup>
                        {customers.map((customer) => (
                          <CommandItem key={customer.id} value={customer.company_name} onSelect={(value) => { setCreateQuotationCompany(value); setCreateQuotationCompanyOpen(false); }} className="text-white hover:bg-gray-800">
                            <Check className={`mr-2 h-4 w-4 ${createQuotationCompany === customer.company_name ? "opacity-100" : "opacity-0"}`} />
                            {customer.company_name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Item Selection - From Box Quotation Calculator */}
            <div>
              <Label className="text-gray-300">Item Name *</Label>
              {availableQuotationsForCompany.length > 0 ? (
                <Popover open={quotationItemOpen} onOpenChange={setQuotationItemOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between bg-gray-800 border-gray-600 text-white mt-1" disabled={!createQuotationCompany}>
                      {selectedQuotationForPrint ? availableQuotationsForCompany.find(q => q.id === selectedQuotationForPrint)?.item_name || availableQuotationsForCompany.find(q => q.id === selectedQuotationForPrint)?.box_name : "Select item..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0 bg-gray-900 border-gray-700">
                    <Command className="bg-gray-900">
                      <CommandInput placeholder="Search item..." className="text-white" />
                      <CommandList>
                        <CommandEmpty>No items found.</CommandEmpty>
                        <CommandGroup>
                          {availableQuotationsForCompany.map((q) => (
                            <CommandItem key={q.id} value={q.item_name || q.box_name} onSelect={() => { setSelectedQuotationForPrint(q.id); setQuotationItemOpen(false); }} className="text-white hover:bg-gray-800">
                              <Check className={`mr-2 h-4 w-4 ${selectedQuotationForPrint === q.id ? "opacity-100" : "opacity-0"}`} />
                              <div className="flex flex-col">
                                <span>{q.item_name || q.box_name}</span>
                                <span className="text-xs text-gray-500">{q.quotation_number} - ₹{q.calculated_values?.finalBoxPrice || '0.00'}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              ) : (
                <div className="mt-1">
                  <Input disabled placeholder={createQuotationCompany ? "No quotations available for this company" : "Select company first"} className="bg-gray-800 border-gray-600 text-gray-400" />
                  {createQuotationCompany && (
                    <p className="text-xs text-yellow-400 mt-1">No in-process quotations found. Create a new quotation first.</p>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => { setShowCreateQuotationDialog(false); setCreateQuotationCompany(''); setSelectedQuotationForPrint(''); }} className="border-gray-600 text-gray-300">
                Cancel
              </Button>
              <Button onClick={handleCreateQuotationPrint} className="bg-green-600 hover:bg-green-500 text-white" disabled={!selectedQuotationForPrint}>
                <Printer className="h-4 w-4 mr-2" />
                Print Proposal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md bg-gray-950 border-2 border-yellow-500/30">
          <DialogHeader>
            <DialogTitle className="text-xl text-yellow-400 flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Authorization Required
            </DialogTitle>
            <DialogDescription className="text-gray-400">Enter password to approve this quotation</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <PasswordInput value={passwordInput} onChange={(e) => setPasswordInput(e.target.value)} placeholder="Enter password..." className="bg-gray-800 border-gray-600 text-white" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setPasswordDialogOpen(false); setPasswordInput(''); setPendingStatusChange(null); }} className="border-gray-600 text-gray-300">Cancel</Button>
              <Button onClick={confirmStatusChange} className="bg-yellow-600 hover:bg-yellow-500 text-white">Confirm</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Quotation Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl bg-gray-950 border-2 border-cyan-500/30">
          <DialogHeader>
            <DialogTitle className="text-2xl text-cyan-400 flex items-center gap-2">
              <Eye className="h-6 w-6" />
              Quotation Details
            </DialogTitle>
          </DialogHeader>
          {viewingQuotation && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><p className="text-gray-400">Quote #</p><p className="text-cyan-400 font-mono">{viewingQuotation.quotation_number || '-'}</p></div>
                <div className="space-y-2"><p className="text-gray-400">Company</p><p className="text-white font-semibold">{viewingQuotation.company_name || '-'}</p></div>
                <div className="space-y-2"><p className="text-gray-400">Item Name</p><p className="text-white font-semibold">{viewingQuotation.item_name || viewingQuotation.box_name}</p></div>
                <div className="space-y-2"><p className="text-gray-400">Ply</p><p className="text-white font-semibold">{viewingQuotation.ply_count} Ply</p></div>
                <div className="space-y-2"><p className="text-gray-400">Joint Type</p><p className="text-white font-semibold">{viewingQuotation.joint_type}</p></div>
                <div className="space-y-2"><p className="text-gray-400">MOQ</p><p className="text-white font-semibold">{viewingQuotation.moq}</p></div>
                <div className="space-y-2"><p className="text-gray-400">Dimensions</p><p className="text-white font-semibold">{viewingQuotation.dimensions?.length || 0} × {viewingQuotation.dimensions?.breadth || 0} × {viewingQuotation.dimensions?.height || 0} {viewingQuotation.dimensions?.unit || 'mm'}</p></div>
                <div className="space-y-2"><p className="text-gray-400">Reel × Cutting</p><p className="text-white font-semibold">{viewingQuotation.reel_size} × {viewingQuotation.cutting_size}</p></div>
              </div>
              <div className="p-4 bg-gray-800/50 rounded-lg">
                <p className="text-gray-400 mb-2">Ply Configuration</p>
                <div className="grid grid-cols-3 gap-2">
                  {viewingQuotation.ply_config?.map((ply, idx) => (
                    <div key={`ply-view-${ply.name}-${idx}`} className={`p-2 rounded ${ply.type === 'liner' ? 'bg-blue-900/30' : 'bg-orange-900/30'}`}>
                      <p className={`text-sm ${ply.type === 'liner' ? 'text-blue-400' : 'text-orange-400'}`}>{ply.name}</p>
                      <p className="text-white text-sm">{ply.shade} - {ply.gsm} GSM</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-6 bg-gradient-to-r from-green-900/30 to-emerald-900/30 rounded-xl border border-green-500/50 text-center">
                <p className="text-gray-300">Final Price</p>
                <p className="text-4xl font-bold text-green-400">₹{viewingQuotation.calculated_values?.finalBoxPrice || '0.00'}</p>
              </div>
              {viewingQuotation.comment && (
                <div className="p-4 bg-gray-800/50 rounded-lg">
                  <p className="text-gray-400 mb-2">Comment</p>
                  <p className="text-white">{viewingQuotation.comment}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Comment Dialog */}
      <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
        <DialogContent className="sm:max-w-md bg-gray-950 border-2 border-yellow-500/30">
          <DialogHeader>
            <DialogTitle className="text-xl text-yellow-400 flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Add Comment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input value={currentComment} onChange={(e) => setCurrentComment(e.target.value)} placeholder="Enter your comment..." className="bg-gray-800 border-gray-600 text-white" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCommentDialogOpen(false)} className="border-gray-600 text-gray-300">Cancel</Button>
              <Button onClick={handleAddComment} className="bg-yellow-600 hover:bg-yellow-500 text-white">Save Comment</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quotation Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-gray-950 border-2 border-cyan-500/30">
          <DialogHeader>
            <DialogTitle className="text-2xl text-cyan-400 flex items-center gap-2">
              <Calculator className="h-6 w-6" />
              {editingQuotation ? `Edit ${productType} Quotation` : `New ${productType} Quotation`}
            </DialogTitle>
            <DialogDescription className="text-gray-400">Configure {productType.toLowerCase()} specifications and calculate pricing</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Legacy-quotation banner — shown when an old-schema quotation is
                opened in the new Box Quotation Calculator. The original system
                only captured per_box_price/quantity; ply config / paper prices
                were never saved, so those fields appear empty by design. */}
            {editingQuotation && (!editingQuotation.ply_config || editingQuotation.ply_config.length === 0) && (
              <div className="rounded-md border-2 border-amber-500/40 bg-amber-500/10 px-4 py-3 text-amber-200 text-sm flex items-start gap-2" data-testid="legacy-quotation-banner">
                <span className="text-xl leading-none">ℹ️</span>
                <div className="space-y-1">
                  <p className="font-semibold text-amber-300">Legacy quotation — ply specifications were never captured</p>
                  <p className="text-amber-200/80 text-xs">
                    This quotation was created in the old system before the Box Quotation Calculator existed. The dimensions, reel size and cutting size are restored above — but the paper specifications (Shade / GSM / Paper Price per ply), printing price, transport cost and lamination details were never saved. Please fill them in to lock the new calculator's pricing. Use "Clone" instead of "Edit" if you want to keep the original record untouched.
                  </p>
                </div>
              </div>
            )}

            {/* Product Type Selection */}
            <Card className="bg-gray-900/50 border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-slate-200 flex items-center gap-2">
                  <Package className="h-5 w-5 text-purple-400" />
                  Product Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <Label className="text-gray-300">Select Product Type *</Label>
                    <Popover open={productTypeOpen} onOpenChange={setProductTypeOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" className="w-full justify-between bg-gray-800 border-gray-600 text-white mt-1">
                          {productType || "Select type..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0 bg-gray-900 border-gray-700">
                        <Command className="bg-gray-900">
                          <CommandInput placeholder="Search type..." className="text-white" />
                          <CommandList>
                            <CommandEmpty>No type found.</CommandEmpty>
                            <CommandGroup>
                              {allProductTypes.map((type) => (
                                <CommandItem key={type} value={type} onSelect={(value) => { setProductType(value); setProductTypeOpen(false); }} className="text-white hover:bg-gray-800">
                                  <Check className={`mr-2 h-4 w-4 ${productType === type ? "opacity-100" : "opacity-0"}`} />
                                  <Package className="mr-2 h-4 w-4 text-purple-400" />
                                  {type}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    {!showAddProductType ? (
                      <Button variant="outline" onClick={() => setShowAddProductType(true)} className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Type
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Input 
                          value={newProductType} 
                          onChange={(e) => setNewProductType(e.target.value)} 
                          placeholder="Enter new type..." 
                          className="bg-gray-800 border-gray-600 text-white w-40" 
                        />
                        <Button 
                          onClick={() => {
                            if (newProductType.trim()) {
                              setCustomProductTypes([...customProductTypes, newProductType.trim()]);
                              setProductType(newProductType.trim());
                              setNewProductType('');
                              setShowAddProductType(false);
                            }
                          }} 
                          className="bg-purple-600 hover:bg-purple-500"
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" onClick={() => { setShowAddProductType(false); setNewProductType(''); }} className="border-gray-600">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Company and Item Selection */}
            <Card className="bg-gray-900/50 border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-slate-200 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-cyan-400" />
                  Company & Item Selection
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Company Name *</Label>
                  <Popover open={formCompanyOpen} onOpenChange={setFormCompanyOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" className="w-full justify-between bg-gray-800 border-gray-600 text-white mt-1">
                        {selectedFormCompany || "Select company..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0 bg-gray-900 border-gray-700">
                      <Command className="bg-gray-900">
                        <CommandInput placeholder="Search company..." className="text-white" />
                        <CommandList>
                          <CommandEmpty>No company found.</CommandEmpty>
                          <CommandGroup>
                            {customers.map((customer) => (
                              <CommandItem key={customer.id} value={customer.company_name} onSelect={(value) => { setSelectedFormCompany(value); setFormCompanyOpen(false); }} className="text-white hover:bg-gray-800">
                                <Check className={`mr-2 h-4 w-4 ${selectedFormCompany === customer.company_name ? "opacity-100" : "opacity-0"}`} />
                                {customer.company_name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-gray-300">Item Name *</Label>
                  {!useManualItemName ? (
                    <Popover open={itemOpen} onOpenChange={setItemOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" className="w-full justify-between bg-gray-800 border-gray-600 text-white mt-1" disabled={!selectedFormCompany}>
                          {itemName || (selectedFormCompany ? "Search from Prod & Materials..." : "Select company first")}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0 bg-gray-900 border-gray-700" align="start">
                        <Command className="bg-gray-900">
                          <CommandInput 
                            placeholder="Search item from Prod & Materials..." 
                            value={itemNameSearch}
                            onValueChange={setItemNameSearch}
                            className="text-white" 
                          />
                          <CommandList>
                            <CommandEmpty>
                              <div className="p-3 text-center">
                                <p className="text-gray-400 text-sm mb-2">
                                  {filteredProductSpecs.length === 0 
                                    ? "No items found for this company in Prod & Materials Info"
                                    : "No matching items found"}
                                </p>
                                {itemNameSearch && (
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      setItemName(itemNameSearch);
                                      setItemOpen(false);
                                      setItemNameSearch('');
                                    }}
                                    className="bg-cyan-600 hover:bg-cyan-700 text-white"
                                  >
                                    Use "{itemNameSearch}" as item name
                                  </Button>
                                )}
                              </div>
                            </CommandEmpty>
                            <CommandGroup heading={`Items from Prod & Materials Info (${filteredProductSpecs.length})`}>
                              {filteredProductSpecs
                                .filter(spec => 
                                  !itemNameSearch || 
                                  spec.item_name?.toLowerCase().includes(itemNameSearch.toLowerCase()) ||
                                  spec.product_id?.toLowerCase().includes(itemNameSearch.toLowerCase())
                                )
                                .slice(0, 50) // Limit to 50 items for performance
                                .map((spec) => (
                                  <CommandItem 
                                    key={spec.id} 
                                    value={`${spec.product_id} ${spec.item_name}`}
                                    onSelect={() => { 
                                      setItemName(spec.item_name);
                                      // Auto-fill other fields from the selected product spec
                                      if (spec.ply) setPlyCount(spec.ply.toString());
                                      if (spec.reel_size || spec.cutting_size) {
                                        // Spec carries known reel/cutting — lock them so the
                                        // auto-compute doesn't recompute them later when the
                                        // user adjusts dimensions on this spec-loaded item.
                                        reelManuallyEditedRef.current = true;
                                      }
                                      if (spec.reel_size) setReelSize(spec.reel_size);
                                      if (spec.cutting_size) setCuttingSize(spec.cutting_size);
                                      if (spec.length || spec.breadth || spec.height) {
                                        setDimensions({
                                          length: spec.length || '',
                                          breadth: spec.breadth || '',
                                          height: spec.height || ''
                                        });
                                      }
                                      setItemOpen(false);
                                      setItemNameSearch('');
                                    }} 
                                    className="text-white hover:bg-gray-800 cursor-pointer py-2"
                                  >
                                    <Check className={`mr-2 h-4 w-4 ${itemName === spec.item_name ? "opacity-100" : "opacity-0"}`} />
                                    <div className="flex flex-col">
                                      <span className="font-medium">{spec.item_name}</span>
                                      <span className="text-xs text-gray-400">
                                        {spec.product_id} | {spec.ply} Ply | {spec.length}×{spec.breadth}×{spec.height}
                                        {spec.rate ? ` | ₹${spec.rate}` : ''}
                                      </span>
                                    </div>
                                  </CommandItem>
                                ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <Input 
                      value={itemName} 
                      onChange={(e) => setItemName(e.target.value)} 
                      placeholder="Enter item name manually" 
                      className="bg-gray-800 border-gray-600 text-white mt-1" 
                    />
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setUseManualItemName(!useManualItemName);
                        if (!useManualItemName) setItemName('');
                      }}
                      className="text-cyan-400 hover:text-cyan-300 text-xs p-0 h-auto"
                    >
                      {useManualItemName ? "← Search from Prod & Materials" : "+ Enter manually"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Box/Layer/Partition Specifications */}
            <Card className="bg-gray-900/50 border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-slate-200 flex items-center gap-2">
                  <Box className="h-5 w-5 text-cyan-400" />
                  {productType} Specifications
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <Label className="text-gray-300">Ply Count</Label>
                  <Select value={plyCount} onValueChange={setPlyCount}>
                    <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['3', '5', '7', '9', '11'].map(p => <SelectItem key={p} value={p}>{p} Ply</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Type of Production</Label>
                  <Popover open={productionTypeOpen} onOpenChange={setProductionTypeOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" className="w-full justify-between bg-gray-800 border-gray-600 text-white mt-1">
                        {productionType}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0 bg-gray-900 border-gray-700">
                      <Command className="bg-gray-900">
                        <CommandInput placeholder="Search production type..." className="text-white" />
                        <CommandList>
                          <CommandEmpty>No production type found.</CommandEmpty>
                          <CommandGroup>
                            {PRODUCTION_TYPE_OPTIONS.map((type) => (
                              <CommandItem key={type} value={type} onSelect={(value) => { setProductionType(value); setProductionTypeOpen(false); }} className="text-white hover:bg-gray-800">
                                <Check className={`mr-2 h-4 w-4 ${productionType === type ? "opacity-100" : "opacity-0"}`} />
                                <Factory className="mr-2 h-4 w-4 text-cyan-400" />
                                {type}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                {productType === 'Partition' ? (
                  <div>
                    <Label className="text-gray-300">No of Partitions</Label>
                    <Input 
                      type="number" 
                      value={numberOfPartitions} 
                      onChange={(e) => setNumberOfPartitions(e.target.value)} 
                      placeholder="Enter number" 
                      min="1"
                      className="bg-gray-800 border-gray-600 text-white mt-1" 
                    />
                    <p className="text-xs text-gray-500 mt-1">Multiplies final price</p>
                  </div>
                ) : (
                  <>
                  <div>
                    <Label className="text-gray-300">Joint Type</Label>
                    <Select value={jointType} onValueChange={setJointType}>
                      <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Single">Single</SelectItem>
                        <SelectItem value="Double">Double</SelectItem>
                        <SelectItem value="Four">Four</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Up's Dropdown */}
                  <div>
                    <Label className="text-gray-300">Up's</Label>
                    <div className="flex gap-2 mt-1">
                      <Select value={ups} onValueChange={setUps}>
                        <SelectTrigger className="bg-gray-800 border-gray-600 text-white flex-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {upsOptions.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt} Up{opt !== '1' ? "'s" : ""}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setShowAddUps(true)}
                        className="bg-gray-700 border-gray-600 text-cyan-400 hover:bg-gray-600"
                        title="Add more Up's options"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Boxes per production unit</p>
                  </div>
                  </>
                )}
                {jointType === 'Four' ? (
                  <>
                    {/* Wall-1 */}
                    <div className="col-span-2 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                      <p className="text-blue-400 font-semibold text-sm mb-2">Wall-1 (Length Boards)</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-gray-300 text-xs">Reel Size ({dimensionUnit})</Label>
                          <Input type="text" inputMode="decimal" value={reelSize} onChange={(e) => { reelManuallyEditedRef.current = true; setReelSize(e.target.value); }} placeholder="Reel size" className="bg-gray-800 border-blue-500/50 text-white mt-1" />
                        </div>
                        <div>
                          <Label className="text-gray-300 text-xs">Cutting Size ({dimensionUnit})</Label>
                          <Input type="text" inputMode="decimal" value={cuttingSize} onChange={(e) => { reelManuallyEditedRef.current = true; setCuttingSize(e.target.value); }} placeholder="Cutting size" className="bg-gray-800 border-blue-500/50 text-white mt-1" />
                        </div>
                      </div>
                      {calculatedValues.wall1Price > 0 && (
                        <p className="text-blue-300 text-xs mt-2">Wall-1 Price: ₹{calculatedValues.wall1Price}</p>
                      )}
                    </div>
                    {/* Wall-2 */}
                    <div className="col-span-2 p-3 bg-purple-900/20 border border-purple-500/30 rounded-lg">
                      <p className="text-purple-400 font-semibold text-sm mb-2">Wall-2 (Breadth Boards)</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-gray-300 text-xs">Reel Size ({dimensionUnit})</Label>
                          <Input type="number" value={wall2ReelSize} onChange={(e) => setWall2ReelSize(e.target.value)} placeholder="Reel size" className="bg-gray-800 border-purple-500/50 text-white mt-1" />
                        </div>
                        <div>
                          <Label className="text-gray-300 text-xs">Cutting Size ({dimensionUnit})</Label>
                          <Input type="number" value={wall2CuttingSize} onChange={(e) => setWall2CuttingSize(e.target.value)} placeholder="Cutting size" className="bg-gray-800 border-purple-500/50 text-white mt-1" />
                        </div>
                      </div>
                      {calculatedValues.wall2Price > 0 && (
                        <p className="text-purple-300 text-xs mt-2">Wall-2 Price: ₹{calculatedValues.wall2Price}</p>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label className="text-gray-300">Reel Size ({dimensionUnit})</Label>
                      <Input type="text" inputMode="decimal" value={reelSize} onChange={(e) => { reelManuallyEditedRef.current = true; setReelSize(e.target.value); }} placeholder="Enter reel size" className="bg-gray-800 border-gray-600 text-white mt-1" />
                    </div>
                    <div>
                      <Label className="text-gray-300">Cutting Size ({dimensionUnit})</Label>
                      <Input type="text" inputMode="decimal" value={cuttingSize} onChange={(e) => { reelManuallyEditedRef.current = true; setCuttingSize(e.target.value); }} placeholder="Enter cutting size" className="bg-gray-800 border-gray-600 text-white mt-1" />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Dimensions */}
            <Card className="bg-gray-900/50 border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-slate-200">Dimensions & MOQ</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-5 gap-4">
                <div>
                  <Label className="text-gray-300">Unit</Label>
                  <Select value={dimensionUnit} onValueChange={setDimensionUnit}>
                    <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mm">mm</SelectItem>
                      <SelectItem value="cm">cm</SelectItem>
                      <SelectItem value="in">inches</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Length</Label>
                  <Input type="number" value={dimensions.length} onChange={(e) => setDimensions({...dimensions, length: e.target.value})} placeholder="0" className="bg-gray-800 border-gray-600 text-white mt-1" />
                </div>
                <div>
                  <Label className="text-gray-300">Breadth</Label>
                  <Input type="number" value={dimensions.breadth} onChange={(e) => setDimensions({...dimensions, breadth: e.target.value})} placeholder="0" className="bg-gray-800 border-gray-600 text-white mt-1" />
                </div>
                <div>
                  <Label className="text-gray-300">Height</Label>
                  <Input type="number" value={dimensions.height} onChange={(e) => setDimensions({...dimensions, height: e.target.value})} placeholder="0" className="bg-gray-800 border-gray-600 text-white mt-1" />
                </div>
                <div>
                  <Label className="text-gray-300">MOQ</Label>
                  <Input type="number" value={moq} onChange={(e) => setMoq(e.target.value)} placeholder="Enter MOQ" className="bg-gray-800 border-gray-600 text-white mt-1" />
                </div>
                <div>
                  <Label className="text-gray-300">Wastage %</Label>
                  <Input 
                    type="number" 
                    value={wastagePercent} 
                    onChange={(e) => setWastagePercent(e.target.value)} 
                    placeholder="5" 
                    className="bg-gray-800 border-gray-600 text-white mt-1"
                    min="0"
                    max="100"
                    step="0.5"
                  />
                  <p className="text-xs text-gray-500 mt-1">Default: 5%</p>
                </div>
                <div>
                  <Label className="text-gray-300">Total Corrugated Rolls</Label>
                  <Input 
                    type="number" 
                    value={totalCorrugatedRolls} 
                    onChange={(e) => setTotalCorrugatedRolls(e.target.value)} 
                    placeholder="Enter number of rolls" 
                    className="bg-gray-800 border-gray-600 text-white mt-1"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Number of rolls needed</p>
                </div>
              </CardContent>
            </Card>

            {/* Ply Configuration */}
            <Card className="bg-gray-900/50 border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-slate-200 flex items-center gap-2">
                  <Layers className="h-5 w-5 text-purple-400" />
                  {plyCount} Ply Configuration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {plyConfig.map((ply, index) => (
                    <div key={`ply-edit-${ply.name}-${index}`} className={`p-4 rounded-lg border ${ply.type === 'liner' ? 'bg-blue-900/20 border-blue-500/30' : 'bg-orange-900/20 border-orange-500/30'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <span className={`font-semibold ${ply.type === 'liner' ? 'text-blue-400' : 'text-orange-400'}`}>{ply.name}</span>
                        {ply.type === 'flute' && <span className="text-xs bg-orange-500/20 text-orange-300 px-2 py-1 rounded">×150%</span>}
                      </div>
                      <div className="space-y-2">
                        <div>
                          <Label className="text-gray-400 text-xs">Shade</Label>
                          <Select value={ply.shade} onValueChange={(v) => updatePlyConfig(index, 'shade', v)}>
                            <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1 h-9"><SelectValue placeholder="Select shade" /></SelectTrigger>
                            <SelectContent>
                              {SHADE_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-gray-400 text-xs">GSM</Label>
                          <Input type="number" value={ply.gsm} onChange={(e) => updatePlyConfig(index, 'gsm', e.target.value)} placeholder="Enter GSM" className="bg-gray-800 border-gray-600 text-white mt-1 h-9" />
                        </div>
                        <div>
                          <Label className="text-gray-400 text-xs">Paper Price (₹)</Label>
                          <Input type="number" value={ply.paperPrice} onChange={(e) => updatePlyConfig(index, 'paperPrice', e.target.value)} placeholder="Enter price" className="bg-gray-800 border-gray-600 text-white mt-1 h-9" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                  <Label className="text-gray-400 text-sm">Total GSM Calculation:</Label>
                  <p className="text-sm text-gray-300 mt-1 font-mono">{getPlyStructureDisplay() || 'Configure GSM values above'}</p>
                  <p className="text-lg text-cyan-400 font-bold mt-2">Total GSM: {calculatedValues.totalGSM}</p>
                </div>
              </CardContent>
            </Card>

            {/* Pricing Inputs (removed Paper Price - now per ply) */}
            <Card className="bg-gray-900/50 border-gray-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-slate-200 flex items-center gap-2">
                  <Calculator className="h-5 w-5 text-green-400" />
                  Pricing Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className={`grid grid-cols-2 ${productType === 'Partition' ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-4`}>
                  <div>
                    <Label className="text-gray-300">Profit Margin</Label>
                    <div className="flex gap-2 mt-1">
                      <Input type="number" value={profitMargin} onChange={(e) => setProfitMargin(e.target.value)} placeholder="30" className="bg-gray-800 border-gray-600 text-white flex-1" />
                      <Select value={profitMarginType} onValueChange={setProfitMarginType}>
                        <SelectTrigger className="w-20 bg-gray-800 border-gray-600 text-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rupees">₹</SelectItem>
                          <SelectItem value="percentage">%</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {productType !== 'Partition' && (
                    <div>
                      <Label className="text-gray-300">Printing Price (₹)</Label>
                      <Input type="number" value={printingPrice} onChange={(e) => setPrintingPrice(e.target.value)} placeholder="0" className="bg-gray-800 border-gray-600 text-white mt-1" />
                    </div>
                  )}
                  <div>
                    <Label className="text-gray-300">Transport Cost (₹)</Label>
                    <Input type="number" value={transportCost} onChange={(e) => setTransportCost(e.target.value)} placeholder="Total cost" className="bg-gray-800 border-gray-600 text-white mt-1" />
                    <p className="text-xs text-gray-500 mt-1">÷ MOQ = Per unit</p>
                  </div>
                </div>
                
                {/* Other Costs Section */}
                <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-white font-medium">Other Costs</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setOtherCosts([...otherCosts, {
                        _uid: (typeof crypto !== "undefined" && crypto.randomUUID
                          ? crypto.randomUUID()
                          : `cost-${Date.now()}-${Math.random().toString(36).slice(2,8)}`),
                        title: '', amount: ''
                      }])}
                      className="bg-cyan-600/20 border-cyan-500/50 text-cyan-400 hover:bg-cyan-600/40"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Cost
                    </Button>
                  </div>
                  
                  {otherCosts.length > 0 ? (
                    <div className="space-y-2">
                      {otherCosts.map((cost, index) => (
                        <div key={cost._uid || `cost-${index}`} className="flex gap-2 items-center">
                          <Input
                            type="text"
                            value={cost.title}
                            onChange={(e) => {
                              const newCosts = [...otherCosts];
                              newCosts[index].title = e.target.value;
                              setOtherCosts(newCosts);
                            }}
                            placeholder="Cost title (e.g., Handling)"
                            className="flex-1 bg-gray-700 border-gray-600 text-white h-9"
                          />
                          <div className="flex items-center gap-1">
                            <span className="text-gray-400">₹</span>
                            <Input
                              type="number"
                              value={cost.amount}
                              onChange={(e) => {
                                const newCosts = [...otherCosts];
                                newCosts[index].amount = e.target.value;
                                setOtherCosts(newCosts);
                              }}
                              placeholder="Amount"
                              className="w-28 bg-gray-700 border-gray-600 text-white h-9"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setOtherCosts(otherCosts.filter((_, i) => i !== index));
                            }}
                            className="h-9 w-9 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <div className="flex justify-between pt-2 border-t border-gray-700 mt-3">
                        <span className="text-gray-400">Total Other Costs:</span>
                        <span className="text-cyan-400 font-bold">
                          ₹{otherCosts.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm text-center py-2">No additional costs added</p>
                  )}
                </div>
                
                <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Switch checked={laminationEnabled} onCheckedChange={setLaminationEnabled} />
                      <Label className="text-white font-medium">Enable Lamination</Label>
                    </div>
                    {laminationEnabled && (
                      <div className="flex items-center gap-2">
                        <Label className="text-gray-400">Vendor Cost (₹):</Label>
                        <Input type="number" value={laminationVendorCost} onChange={(e) => setLaminationVendorCost(e.target.value)} placeholder="Enter cost" className="w-32 bg-gray-700 border-gray-600 text-white h-9" />
                      </div>
                    )}
                  </div>
                  {laminationEnabled && (
                    <div className="mt-3 text-sm text-gray-400">
                      <span className="font-mono">Lamination = {(() => { const toIn = (v) => { const n = parseFloat(v) || 0; return dimensionUnit === 'mm' ? (n / 25.4).toFixed(2) : dimensionUnit === 'cm' ? (n / 2.54).toFixed(2) : n; }; return `${toIn(reelSize)} × ${toIn(cuttingSize)}`; })()} ÷ 100 × {laminationVendorCost || '0'} = <span className="text-green-400 ml-2">₹{calculatedValues.laminationCost}</span></span>
                      {dimensionUnit !== 'in' && <span className="text-cyan-400 text-xs ml-2">(converted to inches)</span>}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Weight & Price Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Weight Tile */}
              <Card className="bg-gradient-to-br from-purple-900/30 to-indigo-900/30 border-2 border-purple-500/50">
                <CardContent className="pt-6">
                  <div className="p-6 bg-purple-900/20 rounded-xl border border-purple-500/30 text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Scale className="h-6 w-6 text-purple-400" />
                      <p className="text-gray-300 text-lg">{productType} Weight</p>
                    </div>
                    <p className="text-4xl font-bold text-purple-400 mt-2">{calculatedValues.boxWeight} KG</p>
                    <p className="text-gray-400 mt-2 text-xs font-mono">
                      {(() => {
                        const toIn = (v) => { const n = parseFloat(v) || 0; return dimensionUnit === 'mm' ? (n / 25.4).toFixed(2) : dimensionUnit === 'cm' ? (n / 2.54).toFixed(2) : n; };
                        return `(((${calculatedValues.totalGSM} × ${toIn(reelSize)} × ${toIn(cuttingSize)}) ÷ 1550) × ${calculatedValues.jointMultiplier || 1}) × ${(1 + parseFloat(wastagePercent || 5) / 100).toFixed(2)} ÷ 1000`;
                      })()}
                    </p>
                    {dimensionUnit !== 'in' && <p className="text-xs text-cyan-400 mt-1">Reel/Cutting auto-converted from {dimensionUnit} to inches</p>}
                    <p className="text-xs text-yellow-400 mt-1">Wastage: {wastagePercent || 5}%</p>
                  </div>
                </CardContent>
              </Card>

              {/* Final Price Tile */}
              <Card className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 border-2 border-cyan-500/50">
                <CardContent className="pt-6">
                  <div className="p-6 bg-gradient-to-r from-green-900/30 to-emerald-900/30 rounded-xl border border-green-500/50 text-center">
                    <p className="text-gray-300 text-lg">Final {productType} Price</p>
                    <p className="text-5xl font-bold text-green-400 mt-2">₹{calculatedValues.finalBoxPrice}</p>
                    <p className="text-gray-400 mt-2">Per unit price</p>
                    {jointType === 'Four' && parseFloat(calculatedValues.wall1Price) > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-700">
                        <p className="text-sm text-gray-400 mb-2">Four Joint Breakdown:</p>
                        <div className="flex justify-center gap-6">
                          <div className="text-blue-400">
                            <p className="text-xs">Wall-1</p>
                            <p className="font-bold">₹{calculatedValues.wall1Price}</p>
                          </div>
                          <span className="text-gray-500 text-2xl">+</span>
                          <div className="text-purple-400">
                            <p className="text-xs">Wall-2</p>
                            <p className="font-bold">₹{calculatedValues.wall2Price}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Save Button */}
            <div className="flex justify-end gap-4">
              <Button variant="outline" onClick={() => { setShowDialog(false); resetForm(); }} className="border-gray-600 text-gray-300">
                Cancel
              </Button>
              <Button onClick={handleSaveQuotation} disabled={loading} className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white px-8">
                <Save className="h-5 w-5 mr-2" />
                {loading ? 'Saving...' : (editingQuotation ? 'Update Quotation' : 'Save Quotation')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Add Up's Dialog */}
      <Dialog open={showAddUps} onOpenChange={setShowAddUps}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-cyan-400">Add New Up's Value</DialogTitle>
            <DialogDescription className="text-gray-400">
              Enter a new Up's value to add to the dropdown list
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300">Up's Value</Label>
              <Input
                type="number"
                min="1"
                value={newUpsValue}
                onChange={(e) => setNewUpsValue(e.target.value)}
                placeholder="e.g., 8"
                className="bg-gray-800 border-gray-600 text-white mt-1"
              />
            </div>
            <p className="text-xs text-gray-500">
              Current options: {upsOptions.join(', ')}
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setShowAddUps(false); setNewUpsValue(''); }} className="border-gray-600 text-gray-300">
              Cancel
            </Button>
            <Button
              onClick={() => {
                const val = parseInt(newUpsValue);
                if (val && val > 0 && !upsOptions.includes(val.toString())) {
                  const newOptions = [...upsOptions, val.toString()].sort((a, b) => parseInt(a) - parseInt(b));
                  setUpsOptions(newOptions);
                  setUps(val.toString());
                  toast.success(`Added ${val} Up's to the list`);
                } else if (upsOptions.includes(val?.toString())) {
                  toast.error('This value already exists');
                } else {
                  toast.error('Please enter a valid number');
                }
                setShowAddUps(false);
                setNewUpsValue('');
              }}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
            >
              Add
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkOrderQuotation;
